import Swiper, { Navigation, Pagination, Autoplay, A11y, EffectFade, EffectCreative, EffectCoverflow } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import 'swiper/css/effect-creative';
import 'swiper/css/effect-coverflow';

const GraphqlData = window.GraphqlData;
const hwSprites = {};

export const LbxSlider = {
  data() {
    return {
      loading: true,
      swiperInstance: null,
      activeDisclaimer: null,
      // When a popup is "sticky" the user opened it via tap/click and it stays
      // open until they tap outside or click the icon again. Hovering off the
      // icon does NOT close a sticky popup, but does close an unsticky one.
      disclaimerSticky: false,
      // Rect of the open disclaimer icon, resolved against .lbx-slider__wrapper.
      disclaimerPos: null,
      activeOffer: {},
      offersData: [],
      config: {},
      lang: 'en-CA',
      source: 'minerva',
      minervaConfig: null,
      staticOffers: [],
      extraSlides: [],
      slideSettings: {},
      activeSlideIndex: 0,
      btActiveTab: '',
      btPendingTab: '',
      btFlipping: false,
      btPaused: false,
      btRail: null,
      btCycle: 0,
      btDealer: { accent: '', fill: '' },
      mdDealer: { accent: '', fill: '' },
      mdOff: true,
      mdHold: true,
      mdBoot: false,
      mdJelly: null,
      hwGlows: {},
      // Last non-null Animated Frame Line config. The overlay is shared by every
      // slide, so when the active one has no line the config goes null and the
      // look would snap back to the CSS defaults WHILE the overlay is still
      // fading out (0.35s) — the solid frame visibly turned into the short
      // travel dash on every slide change. Keeping the last look makes the fade
      // a clean fade.
      lastFrameLine: null,
      // Countdown
      countdownDays: '00',
      countdownHours: '00',
      countdownMinutes: '00',
      countdownSeconds: '00',
      countdownExpired: false,
      countdownVisible: true,
      countdownInterval: null,
      isMobile: (typeof window !== 'undefined') ? window.innerWidth < 1024 : false,
      isSmallScreen: (typeof window !== 'undefined') ? window.innerWidth <= 640 : false,
      // Current responsive bucket for per-slide visibility ('mobile'|'tablet'|'desktop').
      currentBucket: (typeof window !== 'undefined')
        ? (window.innerWidth <= 640 ? 'mobile' : (window.innerWidth < 1024 ? 'tablet' : 'desktop'))
        : 'desktop',
    };
  },
  computed: {
    isFrench() {
      return this.lang === 'fr-CA';
    },
    hasOffers() {
      return this.offersData.length > 0;
    },
    readyEmpty() {
      return !this.loading && this.offersData.length === 0;
    },
    readyWithOffers() {
      return !this.loading && this.offersData.length > 0;
    },
    isMinerva() {
      return this.source === 'minerva';
    },
    // True when any disclaimer popup is open. Used to flip a root-level class
    // so CSS can promote the swiper container above the navigation arrows —
    // Swiper creates its own stacking context, so the popup's local z-index
    // is otherwise trapped below sibling elements like the arrows.
    isDisclaimerActive() {
      return this.activeDisclaimer !== null;
    },

    disclaimerPopupStyle() {
      var p = this.disclaimerPos;
      if (!p) return {};
      var s = {};
      if (p.placement === 'top-left' || p.placement === 'top-right') s.top = p.y + 'px';
      else s.bottom = p.y + 'px';
      if (p.placement === 'top-left' || p.placement === 'bottom-left') s.left = p.x + 'px';
      else s.right = p.x + 'px';
      return s;
    },
    slideHeightStyle() {
      var h = this.config.sliderHeight || 480;
      var val = (typeof h === 'string' && /[a-z%]/.test(h)) ? h : h + 'px';
      return { height: val };
    },
    heroHeightStyle() {
      var h = this.config.sliderHeight || 480;
      var val = (typeof h === 'string' && /[a-z%]/.test(h)) ? h : h + 'px';
      return { height: val };
    },
    contentAlignClass() {
      var align = this.config.contentAlign || 'left';
      return 'lbx-align--' + align;
    },
    visibleOffers() {
      var self = this;
      return this.offersData.filter(function (o) { return self.isSlideVisibleInBucket(o); });
    },
    activeOfferItem() {
      return this.visibleOffers[this.activeSlideIndex] || null;
    },
    isBrandTabs() {
      return this.config.layout === 'brand-tabs';
    },
    isMotion() {
      return this.config.layout === 'motion';
    },
    motionTemplate() {
      var tpl = this.config.motionTemplate;
      return tpl === 'kinetic' || tpl === 'ignition' || tpl === 'halloween' ? tpl : 'spot';
    },
    motionSeason() {
      return this.isMotion && this.motionTemplate !== 'halloween' && this.config.motionSeason === 'halloween' ? 'halloween' : '';
    },
    seasonTape() {
      return new Array(25).join('HALLOWEEN \u25C6 ');
    },
    motionStyle() {
      var secs = this.config.autoplay ? (parseInt(this.config.autoplaySpeed, 10) || 5000) / 1000 : 7;
      var hw = this.motionTemplate === 'halloween';
      var st = { '--md': Math.min(Math.max(secs, hw ? 9 : 4), hw ? 14 : 12) + 's' };
      if (this.mdDealer.accent) st['--md-accent'] = this.mdDealer.accent;
      if (this.mdDealer.fill) st['--md-fill'] = this.mdDealer.fill;
      if (this.mdDealer.glow) st['--md-glow'] = this.mdDealer.glow;
      if (this.mdDealer.night) st['--md-night'] = this.mdDealer.night;
      if (this.mdDealer.accentLight) {
        st['--md-on-accent'] = '#0f172a';
        st['--md-on-accent-soft'] = 'rgba(15, 23, 42, 0.72)';
        st['--md-accent-text'] = '#0f172a';
      }
      if (this.mdDealer.fillLight) st['--md-on-fill'] = '#0f172a';
      if (this.config.sliderHeight) st.height = this.heroHeightStyle.height;
      return st;
    },
    motionItems() {
      var self = this;
      if (!this.isMotion) return [];
      return this.offersData.map(function (o) { return self.isMotionOffer(o) ? self.motionBuild(o) : null; });
    },
    hwTitle() {
      return this.t('Spooky Deals', 'Offres d’épouvante').split(' ');
    },
    hwTitleEm() {
      return this.hwTitle.reduce(function (max, w, i) { return Math.max(max, w.length * 0.47 + (i ? 0.62 : 0) + 0.3); }, 1);
    },
    hwMonth() {
      var month = '';
      try {
        month = new Intl.DateTimeFormat(this.isFrench ? 'fr-CA' : 'en-CA', { month: 'long' }).format(new Date());
      } catch (e) {
        month = '';
      }
      return month ? this.t('In ', 'En ') + month : '';
    },
    hwRansom() {
      var text = this.hwMonth;
      var s = 4271;
      var rnd = function () { s = (s * 16807) % 2147483647; return (s - 1) / 2147483646; };
      var order = [];
      for (var i = 0; i < text.length; i++) order.push(i);
      for (var j = order.length - 1; j > 0; j--) {
        var k = Math.floor(rnd() * (j + 1));
        var swap = order[j];
        order[j] = order[k];
        order[k] = swap;
      }
      return text.split('').map(function (ch, idx) {
        if (ch === ' ') return { ch: '', cls: 'lbx-hw__l lbx-hw__l--gap', style: {} };
        return {
          ch: rnd() < 0.5 ? ch.toUpperCase() : ch.toLowerCase(),
          cls: 'lbx-hw__l lbx-hw__l--v' + (1 + Math.floor(rnd() * 5)),
          style: { '--r': ((rnd() - 0.5) * 14).toFixed(1) + 'deg', '--y': ((rnd() - 0.5) * 0.16).toFixed(2) + 'em', '--s': (0.86 + rnd() * 0.28).toFixed(2), '--o': order[idx] },
        };
      });
    },
    btConfig() {
      return (this.config && this.config.brandTabs) || { tabs: [] };
    },
    btCounts() {
      var counts = {};
      if (!this.isBrandTabs) return counts;
      var self = this;
      this.offersData.forEach(function (o) {
        if (!self.isSlideInBucket(o)) return;
        var id = self.btTabOf(o);
        counts[id] = (counts[id] || 0) + 1;
      });
      return counts;
    },
    btTabs() {
      var counts = this.btCounts;
      return (this.btConfig.tabs || []).filter(function (t) { return counts[t.id] > 0; });
    },
    btActive() {
      var tabs = this.btTabs;
      for (var i = 0; i < tabs.length; i++) {
        if (tabs[i].id === this.btActiveTab) return this.btActiveTab;
      }
      return tabs.length ? tabs[0].id : '';
    },
    btSlideCount() {
      return this.btCounts[this.btActive] || 0;
    },
    btSingle() {
      return this.btTabs.length < 2;
    },
    btShowProgress() {
      return !!this.config.showPagination && this.btSlideCount > 1;
    },
    btLitTab() {
      return this.btPendingTab || this.btActive;
    },
    btRatio() {
      var first = this.btFirstOfferOf(this.btActive);
      var pic = first ? this.heroPicture(first) : {};
      return pic.w && pic.h ? pic.w / pic.h : 0;
    },
    btVars() {
      var v = {};
      var fill = this.btConfig.ctaBg || this.btDealer.fill;
      if (this.btDealer.accent) v['--bt-accent'] = this.btDealer.accent;
      if (fill) v['--bt-fill'] = fill;
      if (this.btRatio) v['--lbx-img-ratio'] = this.btRatio;
      return v;
    },
    btHeadingStyle() {
      return this.btConfig.headingColor ? { color: this.btConfig.headingColor } : {};
    },
    btCta() {
      var lit = this.btLitTab;
      var tab = this.btTabs.filter(function (t) { return t.id === lit; })[0] || {};
      return { label: tab.ctaLabel || this.btConfig.ctaLabel || '', url: tab.ctaUrl || this.btConfig.ctaUrl || '' };
    },
    btCtaStyle() {
      var st = {};
      if (this.btConfig.ctaBg) st.backgroundColor = this.btConfig.ctaBg;
      if (this.btConfig.ctaColor) st.color = this.btConfig.ctaColor;
      return st;
    },
    btRailStyle() {
      var r = this.btRail;
      if (!r) return { opacity: 0 };
      return r.horizontal
        ? { transform: 'translate(' + r.x + 'px, ' + r.y + 'px)', width: r.w + 'px' }
        : { transform: 'translateY(' + r.y + 'px)', height: r.h + 'px' };
    },
    activeAlignClass() {
      var offer = this.activeOfferItem;
      if (offer) {
        var cls = this.slideAlignClass(offer);
        return cls;
      }
      return this.contentAlignClass;
    },
    /**
     * Animated Frame Line — PER-SLIDE effect. Each slide carries its own full
     * settings object ({enabled, speed, length, width, color, glow, outline}):
     * extras/statics bring `frame_line` in their payload, OEM offers store it in
     * slideSettings. Returns the ACTIVE slide's config, or null (no line).
     */
    frameLineConfig() {
      var offer = this.activeOfferItem;
      if (!offer) return null;
      var fl = null;
      if (offer.is_extra || offer.is_static) {
        fl = offer.frame_line || null;
      } else if (this.isMinerva) {
        var so = this.slideOverride(offer);
        fl = (so && so.frame_line) || null;
      }
      return (fl && typeof fl === 'object' && fl.enabled) ? fl : null;
    },
    // The look to paint with. While the line is fading out (no line on the
    // active slide) we keep the PREVIOUS slide's look so only the opacity
    // changes — see `lastFrameLine`.
    frameLineLook() {
      return this.frameLineConfig || this.lastFrameLine;
    },
    frameLineClasses() {
      var look = this.frameLineLook;
      return {
        'lbx-slider__frame-line--off': !this.frameLineConfig,
        'lbx-slider__frame-line--glow': !!(look && look.glow),
        'lbx-slider__frame-line--outline': !!(look && look.outline),
        // Draw once & stay: the stroke draws itself in (top center → down both
        // sides → hugging the logo) and keeps the full frame, no looping.
        'lbx-slider__frame-line--draw': !!(look && look.style === 'draw'),
      };
    },
    frameLineStyle() {
      var look = this.frameLineLook;
      if (!look) return {};
      return {
        '--fl-color': look.color || '#ffffff',
        '--fl-width': (parseFloat(look.width) || 4) + 'px',
        '--fl-speed': this.frameLineSpeed + 's',
      };
    },
    /**
     * How long the animation runs, in seconds.
     *
     * "Draw once & stay" only looks right if it REACHES the closing point — and
     * it restarts from zero every time the slide is revisited. With autoplay
     * shorter than the configured speed the frame therefore never closed: it
     * got cut mid-draw, faded out, and started over on the next pass. So the
     * draw is capped to the time the slide is actually on screen (the autoplay
     * delay, minus Swiper's 300ms transition and a small margin). Travel mode
     * loops forever and needs no cap.
     */
    frameLineSpeed() {
      var look = this.frameLineLook;
      var speed = (look && parseFloat(look.speed)) || 9;
      if (!look || look.style !== 'draw' || !this.config.autoplay) return speed;
      var onScreen = ((parseInt(this.config.autoplaySpeed, 10) || 5000) / 1000) - 0.3 - 0.2;
      // Below ~0.6s the draw reads as a flash, so a very tight autoplay keeps
      // the author's speed rather than degrading into a blink.
      return onScreen >= 0.6 ? Math.min(speed, onScreen) : speed;
    },
    frameLineDash() {
      var look = this.frameLineLook;
      // Draw mode strokes the ENTIRE run: dash = full path, offset animates 100→0.
      if (look && look.style === 'draw') return '100 100';
      var len = look ? (parseInt(look.length, 10) || 18) : 18;
      return len + ' ' + (100 - len);
    },
    splitPanelStyle() {
      const hex = (this.config.gradientColor || '#000032').replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16) || 0;
      const g = parseInt(hex.substring(2, 4), 16) || 0;
      const b = parseInt(hex.substring(4, 6), 16) || 0;
      return { backgroundColor: `rgba(${r},${g},${b},0.95)` };
    },
    heroBgPosition() {
      var align = this.config.contentAlign || 'left';
      if (align === 'right') return { objectPosition: 'left center' };
      if (align === 'center') return { objectPosition: 'center center' };
      return { objectPosition: 'right center' };
    },
    heroGradientStyle() {
      return this.heroGradientFor(null);
    },
    watermarkSizeStyle() {
      var s = this.config.watermarkSize || 50;
      return { width: s + 'px', height: 'auto' };
    },
    isCountdown() {
      return this.config.layout === 'countdown';
    },
    arrowCustomStyle() {
      var style = {};
      var color = this.config.arrowColor;
      var opacity = (this.config.arrowOpacity != null ? this.config.arrowOpacity : 90) / 100;
      if (color && this.config.arrowStyle !== 'minimal') {
        var h = color.replace('#', '');
        var r = parseInt(h.substring(0, 2), 16);
        var g = parseInt(h.substring(2, 4), 16);
        var b = parseInt(h.substring(4, 6), 16);
        style.background = 'rgba(' + r + ',' + g + ',' + b + ',' + opacity + ')';
        var luma = (r * 299 + g * 587 + b * 114) / 1000;
        style.color = luma > 128 ? '#374151' : '#ffffff';
      } else if (!color && this.config.arrowStyle !== 'minimal' && this.config.arrowStyle !== 'dark') {
        style.background = 'rgba(255,255,255,' + opacity + ')';
      }
      return style;
    },
    countdownHeightStyle() {
      var h = this.config.sliderHeight || 480;
      return { height: /^\d+$/.test(String(h)) ? h + 'px' : h };
    },
    countdownClasses() {
      var cls = 'lbx-countdown--' + (this.config.countdownStyle || 'flip');
      if (this.countdownExpired) cls += ' lbx-countdown--expired';
      return cls;
    },
    countdownOverlayStyle() {
      var op = (this.config.countdownOverlayOpacity != null ? this.config.countdownOverlayOpacity : 60) / 100;
      return { background: 'rgba(0,0,0,' + op + ')' };
    },
    countdownRootStyle() {
      var style = {};
      if (this.config.countdownBgColor) style.backgroundColor = this.config.countdownBgColor;
      if (this.config.countdownAccent) style['--cd-accent'] = this.config.countdownAccent;
      return style;
    },
    countdownShowOverlay() {
      return !!this.countdownBgUrl || !this.config.countdownBgColor;
    },
    countdownBgUrl() {
      if (this.config.countdownBgImage) return this.config.countdownBgImage;
      if (this.offersData.length > 0 && this.offersData[0].image) return this.offersData[0].image;
      return '';
    },
    countdownTitle() {
      if (this.offersData.length > 0) return this.offersData[0].title || '';
      return '';
    },
    countdownDesc() {
      if (this.offersData.length > 0) return this.offersData[0].description || '';
      return '';
    },
    isMediaColumnsLayout() {
      return this.config.layout === 'media-columns';
    },
    countdownCtas() {
      if (this.offersData.length > 0) return this.offersData[0];
      return null;
    },
  },
  watch: {
    btTabs(tabs) {
      var self = this;
      this.$nextTick(function () { self.btMeasureRail(); });
      if (tabs.length > 1 && !this._btWarm) {
        this._btWarm = true;
        setTimeout(function () {
          tabs.forEach(function (t) {
            var o = t.id === self.btActive ? null : self.btFirstOfferOf(t.id);
            var url = o ? self.heroImage(o) : '';
            if (url) self.btPreload(url);
          });
        }, 1200);
      }
    },
    // The frame line's bottom gap depends on the ACTIVE slide (its watermark
    // visibility) and on the slide list arriving async — redraw on both.
    activeSlideIndex() {
      if (this._frameLineDraw) this._frameLineDraw();
      // Entering a slide with a "draw once & stay" line: replay its draw-in.
      var fl = this.frameLineConfig;
      if (fl && fl.style === 'draw' && this._frameLineRestart) {
        this._frameLineRestart();
      }
    },
    // `immediate` so the very first slide's look is banked before the slider can
    // move off it — otherwise the first fade-out would have nothing to fall back on.
    frameLineConfig: {
      immediate: true,
      handler(fl) {
        // Remember the look so the fade-out keeps it (see `lastFrameLine`).
        if (fl) this.lastFrameLine = fl;
        if (this._frameLineDraw) this._frameLineDraw();
      },
    },
  },
  mounted() {
    const instanceId = this.$el.getAttribute('data-instance');

    if (!instanceId) {
      console.error('[LbxSlider] No data-instance found on root element');
      this.loading = false;
      return;
    }

    const dataEl = document.getElementById(instanceId + '-data');

    if (!dataEl) {
      this.loading = false;
      return;
    }

    try {
      const payload = JSON.parse(dataEl.textContent);
      this.config = payload.config || {};
      this.lang = payload.lang || 'en-CA';
      this.source = payload.source || 'minerva';

      // Animated Frame Line — the Blade renders the overlay SVG when any slide
      // enables it; draw the two mirrored half-frames and keep them sized to the
      // slider. Runs right after config is parsed (the bottom gap needs the
      // watermark settings) and before the per-source branches return.
      this.initFrameLine();

      // Now that configurable breakpoints are available, recompute the viewport flags
      // (data() seeded them with the 640/1024 defaults before config was parsed).
      if (typeof window !== 'undefined') {
        this.isMobile = window.innerWidth < this.bpTablet();
        this.isSmallScreen = window.innerWidth <= this.bpMobile();
        this.currentBucket = this.bucketForWidth(window.innerWidth);
      }

      if (this.isCountdown) {
        this.offersData = payload.offers || [];
        this.loading = false;
        this.startCountdown();
        this.hideSSR();
        return;
      }

      if (this.source === 'minerva') {
        this.minervaConfig = payload.minerva || {};
        this.extraSlides = payload.extra_slides || [];
        this.slideSettings = payload.slide_settings || {};
        this.staticOffers = payload.staticOffers || [];

        // LBT-1472: OEM switch controls whether OEM offers are pulled at all. When OFF,
        // never load OEM (no SSR offers, no client GraphQL fetch) — only manual/static
        // slides render via unifiedSlides. (Static is gated separately in processMinervaOffers.)
        var autoOem = (this.config && this.config.autoPopulateOem !== false);
        var ssrOffers = payload.offers || [];
        if (!autoOem) {
          this.processMinervaOffers([]);
        } else if (payload.ssrReady && ssrOffers.length > 0) {
          // If server-side cached offers are available, use them directly (skip GraphQL fetch)
          this.processMinervaOffers(ssrOffers);
        } else {
          this.fetchMinervaOffers();
        }
      } else {
        this.offersData = payload.offers || [];
        this.loading = false;
        // hideSSR runs inside scheduleInitSwiper, after the carousel is laid out.
        this.$nextTick(() => this.scheduleInitSwiper());
      }
    } catch (e) {
      console.error('LbxSlider: Failed to parse data', e);
      this.loading = false;
    }

    document.addEventListener('click', this.handleOutsideClick);

    // Esc closes any open disclaimer popup (accessibility + parity with the
    // outside-click behavior). Stored on `this` so beforeDestroy can detach it.
    this._onEscKey = function(e) {
      if (e.key === 'Escape' && this.activeDisclaimer !== null) {
        this.closeDisclaimer();
      }
    }.bind(this);
    document.addEventListener('keydown', this._onEscKey);

    // Track viewport changes for video autoplay behavior
    this._lastMobile = this.isMobileViewport();
    this._onResize = function() {
      this.isSmallScreen = window.innerWidth <= this.bpMobile();
      if (this.isBrandTabs) this.btMeasureRail();
      if (this.isMotion) this.mdMeasureWatermark();
      this.closeDisclaimer();
      // Contain/fluid slides derive their height from the image aspect ratio (--lbx-img-ratio),
      // set once on image load. When resizing across viewports (esp. when the mobile↔desktop
      // image swaps), that ratio goes stale and Swiper keeps an old height → blank space below.
      // Re-read the ratios and let Swiper recompute, throttled to one rAF per resize burst.
      var self = this;
      if (this._resizeRaf) cancelAnimationFrame(this._resizeRaf);
      this._resizeRaf = requestAnimationFrame(function () {
        self.refreshImageRatios();
        if (self.swiperInstance && typeof self.swiperInstance.update === 'function') self.swiperInstance.update();
      });
      // Per-slide responsive visibility: if we crossed into a different bucket, the v-if set
      // of slides changes — rebuild Swiper so its internal slide list stays correct.
      var bucket = this.bucketForWidth(window.innerWidth);
      if (bucket !== this.currentBucket) {
        this.currentBucket = bucket;
        this.rebuildForBucket();
      }
      var mobile = this.isMobileViewport();
      if (mobile === this._lastMobile) return;
      this._lastMobile = mobile;
      this.isMobile = mobile;
      this.syncVideoPlayback();
    }.bind(this);
    window.addEventListener('resize', this._onResize);

    if (this.isBrandTabs) {
      var btSelf = this;
      this.btReadDealerColors();
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { btSelf.btMeasureRail(); });
      if ('ResizeObserver' in window && this.$refs.btTotem) {
        this._btRO = new ResizeObserver(function () { btSelf.btMeasureRail(); });
        this._btRO.observe(this.$refs.btTotem);
      }
      if ('IntersectionObserver' in window) {
        this._btIO = new IntersectionObserver(function (entries) {
          var ap = btSelf.swiperInstance && btSelf.swiperInstance.autoplay;
          if (!btSelf.config.autoplay || !ap || !entries[0]) return;
          if (entries[0].isIntersecting) {
            if (!ap.running) ap.start();
            btSelf.btPaused = false;
            btSelf.btCycle++;
          } else {
            if (ap.running) ap.stop();
            btSelf.btPaused = true;
          }
        }, { threshold: 0.15 });
        this._btIO.observe(this.$el);
      }
    }

    if (this.isMotion) {
      var mdSelf = this;
      this.$nextTick(function () { mdSelf.mdReadDealerColors(); });
      this._mdCount = this.motionCountStart.bind(this);
      this.$el.addEventListener('animationstart', this._mdCount);
      if ('IntersectionObserver' in window) {
        this._mdIO = new IntersectionObserver(function (entries) {
          var on = !!(entries[0] && entries[0].isIntersecting);
          mdSelf.mdOff = !on;
          var ap = mdSelf.swiperInstance && mdSelf.swiperInstance.autoplay;
          if (!mdSelf.config.autoplay || !ap) return;
          if (on && !ap.running && !mdSelf.mdHold) ap.start();
          if (!on && ap.running) ap.stop();
        }, { threshold: 0.2 });
        this._mdIO.observe(this.$el);
      } else {
        this.mdOff = false;
      }
    }
  },
  updated() {
    // Slides (and their embeds) arrive asynchronously, so activate on every
    // re-render. The per-script tag makes this a no-op once they've run.
    this.activateEmbedScripts();
  },
  beforeDestroy() {
    this.hwStop();
    (this._hwBlobs || []).forEach(function (url) { URL.revokeObjectURL(url); });
    if (this._btIO) this._btIO.disconnect();
    if (this._mdIO) this._mdIO.disconnect();
    if (this._mdCount) this.$el.removeEventListener('animationstart', this._mdCount);
    if (this._btRO) this._btRO.disconnect();
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
    }
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    document.removeEventListener('click', this.handleOutsideClick);
    if (this._onEscKey) document.removeEventListener('keydown', this._onEscKey);
    if (this._onResize) window.removeEventListener('resize', this._onResize);
    if (this._resizeRaf) cancelAnimationFrame(this._resizeRaf);
    if (this._disclaimerCloseTimer) clearTimeout(this._disclaimerCloseTimer);
    if (this._frameLineObserver) this._frameLineObserver.disconnect();
    if (this._frameLineResize) window.removeEventListener('resize', this._frameLineResize);
  },
  methods: {
    /**
     * Animated Frame Line — draws the two mirrored half-frame paths of the
     * overlay SVG (rendered by the Blade when the feature is enabled) and keeps
     * them sized to the slider. Both halves are born at the TOP CENTER and meet
     * at the BOTTOM CENTER (A travels right, B mirrors left); they share one CSS
     * animation, so they stay synchronized by construction. pathLength="100"
     * normalizes the dash units to % of the run at any resolution.
     */
    initFrameLine() {
      var fl = this.$el.querySelector('.lbx-slider__frame-line');
      if (!fl) return;
      var self = this;
      var draw = function () {
        var w = self.$el.clientWidth, h = self.$el.clientHeight;
        if (!w || !h) return;
        // Proportional inset (matches the approved demo): 8.5% vertical, 1.4% lateral.
        var ix = Math.max(14, w * 0.014), iy = Math.max(16, h * 0.085);
        var cx = w / 2;
        // Bottom-center watermark: the two lines close in JUST BEFORE touching the
        // logo instead of meeting at the center — a symmetric gap of half the
        // watermark width plus breathing room, so the line "hugs" the logo. Only
        // when the active slide actually shows the watermark.
        var gap = 0;
        var cfg = self.config || {};
        if (cfg.watermarkImage && cfg.watermarkPosition === 'bottom-center') {
          var offer = (self.offersData || [])[self.activeSlideIndex];
          var shown = offer ? self.showWatermark(offer) : true;
          if (shown) gap = ((parseFloat(cfg.watermarkSize) || 50) / 2) + 14;
        }
        var ps = fl.querySelectorAll('path');
        if (ps.length < 2) return;
        ps[0].setAttribute('d', 'M' + cx + ' ' + iy + ' H' + (w - ix) + ' V' + (h - iy) + ' H' + (cx + gap));
        ps[1].setAttribute('d', 'M' + cx + ' ' + iy + ' H' + ix + ' V' + (h - iy) + ' H' + (cx - gap));
      };
      // Exposed so watchers can redraw when the active slide (or its watermark
      // visibility) changes — the bottom gap depends on both.
      this._frameLineDraw = draw;
      // Restarts the paths' CSS animation — used when entering a slide whose
      // line is "draw once & stay", so the draw-in replays on each visit.
      this._frameLineRestart = function () {
        var ps = fl.querySelectorAll('path');
        for (var i = 0; i < ps.length; i++) {
          ps[i].style.animation = 'none';
          // getBoundingClientRect, NOT offsetWidth: these are SVG paths and
          // offsetWidth is an HTMLElement property — on an SVGElement it reads
          // undefined and forces no reflow, so the 'none' never commits and the
          // animation just carries on. This used to go unnoticed because the
          // --draw class came off between slides and coming back changed
          // animation-name, which restarts an animation on its own.
          void ps[i].getBoundingClientRect();
          ps[i].style.animation = '';
        }
      };
      if (typeof ResizeObserver !== 'undefined') {
        this._frameLineObserver = new ResizeObserver(draw);
        this._frameLineObserver.observe(this.$el);
      } else {
        this._frameLineResize = draw;
        window.addEventListener('resize', draw);
      }
      draw();
    },

    /**
     * Fetch offers from Minerva GraphQL (same logic as special-offers.vue.js)
     */
    fetchMinervaOffers() {
      if (!GraphqlData) {
        console.error('LbxSlider: GraphqlData not available');
        this.loading = false;
        return;
      }

      const { manufacturer, allBrands, graphqlEndpoint, province } = this.minervaConfig;

      GraphqlData.authenticateAndFetchData(null, 'special-offers', null, null, graphqlEndpoint)
        .then((result) => {
          let offers = result || [];

          // Drop "not a vehicle" promos (service, accessories…). They carry no
          // year/model, so there's nothing to build a vehicle slide from. The
          // special-offers showcase still shows them — this is slider-only.
          offers = offers.filter(item => !(item.offers && item.offers.notAVehicle));

          // Filter by manufacturer and other brands
          let offersMakes = manufacturer ? manufacturer.toLowerCase() : '';
          if (allBrands) {
            offersMakes = offersMakes + ',' + allBrands.toLowerCase();
          }
          offers = offers.filter(item =>
            offersMakes.includes(item.offers.make.toLowerCase())
          );

          // "Show Only {Make} Slides" — multi-make dealers can pin the slider to one
          // make. SSR offers arrive pre-filtered from the Blade; this covers the
          // client-side fetch fallback. ('' = all makes.)
          const makeFilter = String((this.config && this.config.makeFilter) || '').toLowerCase();
          if (makeFilter) {
            offers = offers.filter(item =>
              ((item.offers.make || '').toLowerCase().trim() === makeFilter)
            );
          }

          // Filter by start date
          const today = new Date();
          offers = offers.filter(item => {
            if (!item.offers.startDate) return true;
            const startDate = new Date(item.offers.startDate.split('/').reverse().join('-'));
            return startDate <= today;
          });

          // Filter by end date (exclude offers expiring today)
          offers = offers.filter(item => {
            if (!item.offers.endDate) return true;
            const endDate = new Date(item.offers.endDate.split('/').reverse().join('-'));
            return endDate > today;
          });

          // Filter by province for Ford
          if (manufacturer && manufacturer.toLowerCase() === 'ford') {
            const dealerProvince = province ? province.toUpperCase() : '';
            offers = offers.filter(item => {
              if (!item.offers.province || !item.offers.province.length) return true;
              if (dealerProvince === 'QC') return item.offers.province.includes('QC');
              return item.offers.province.includes('NATIONAL');
            });
          }

          // Deduplicate by year+make+model
          var seen = {};
          offers = offers.filter(function (item) {
            var key = (item.offers.year + '|' + item.offers.make + '|' + item.offers.model).toLowerCase();
            if (seen[key]) return false;
            seen[key] = true;
            return true;
          });

          // Sort by make asc, model asc, year desc
          offers.sort(function (a, b) {
            var makeA = (a.offers.make || '').toLowerCase();
            var makeB = (b.offers.make || '').toLowerCase();
            if (makeA < makeB) return -1;
            if (makeA > makeB) return 1;
            var modelA = (a.offers.model || '').toLowerCase();
            var modelB = (b.offers.model || '').toLowerCase();
            if (modelA < modelB) return -1;
            if (modelA > modelB) return 1;
            return (parseInt(b.offers.year) || 0) - (parseInt(a.offers.year) || 0);
          });

          this.processMinervaOffers(offers);
        })
        .catch((error) => {
          console.error('LbxSlider: Minerva fetch error', error.message);
          this.loading = false;
          this.hideSSR();
        });
    },

    /**
     * Process and finalize Minerva offers (shared by SSR pre-populated and client-fetch paths)
     */
    processMinervaOffers(offers) {
      var ss = this.slideSettings || {};
      var keyOf = function (o) {
        var inner = o.offers || o;
        return [(inner.year || ''), (inner.make || ''), (inner.model || '')]
          .filter(Boolean).join('_').toLowerCase().replace(/\s+/g, '-');
      };

      // Filter hidden Minerva offers based on admin slide_settings (extras are never auto-hidden).
      if (Object.keys(ss).length > 0) {
        offers = offers.filter(function (node) {
          var setting = ss[keyOf(node)];
          return !setting || setting.visible !== false;
        });
      }

      var unified = (this.config && Array.isArray(this.config.unifiedSlides)) ? this.config.unifiedSlides : [];
      // Static offers (image-only Minerva promos) keyed by id — needed by BOTH paths below.
      var staticMap = {};
      (this.staticOffers || []).forEach(function (so) { if (so && so.id != null) staticMap[String(so.id)] = so; });
      var autoStatic = !!(this.config && this.config.autoPopulateStatic);
      var finalSlides;

      if (unified.length > 0) {
        // ─── New path: unifiedSlides is the authoritative order ───────────────
        var offerMap = {};
        offers.forEach(function (o) { offerMap[keyOf(o)] = o; });
        var extrasMap = {};
        (this.extraSlides || []).forEach(function (ex) { if (ex && ex.id) extrasMap[ex.id] = ex; });

        finalSlides = [];
        unified.forEach(function (u) {
          if (u.type === 'minerva' && offerMap[u.key]) {
            finalSlides.push(offerMap[u.key]);
            delete offerMap[u.key];
          } else if (u.type === 'extra' && extrasMap[u.id]) {
            finalSlides.push(extrasMap[u.id]);
            delete extrasMap[u.id];
          } else if (u.type === 'static' && staticMap[u.key]) {
            finalSlides.push(staticMap[u.key]);
            delete staticMap[u.key];
          }
        });
        // Append any Minerva offers or extras not referenced in unified (newly added
        // upstream or after the slider was last saved). They land at the end so the
        // operator can see them and decide where to place them on next edit.
        // NOTE: static offers are NOT appended — they show only when explicitly added
        // to the unified order in the admin.
        Object.keys(offerMap).forEach(function (k) { finalSlides.push(offerMap[k]); });
        Object.keys(extrasMap).forEach(function (k) { finalSlides.push(extrasMap[k]); });
      } else {
        // ─── Legacy path: minervaOrder + extras[].position ─────────────────────
        var order = (this.config && Array.isArray(this.config.minervaOrder)) ? this.config.minervaOrder : [];
        if (order.length > 0) {
          var indexMap = {};
          order.forEach(function (k, i) { indexMap[k] = i; });
          offers = offers.slice().sort(function (a, b) {
            var ia = (keyOf(a) in indexMap) ? indexMap[keyOf(a)] : Number.MAX_SAFE_INTEGER;
            var ib = (keyOf(b) in indexMap) ? indexMap[keyOf(b)] : Number.MAX_SAFE_INTEGER;
            return ia - ib;
          });
        }
        if (this.extraSlides && this.extraSlides.length > 0) {
          var extras = this.extraSlides.slice().sort(function (a, b) { return b.position - a.position; });
          extras.forEach(function (ex) {
            var pos = Math.min(Math.max((ex.position || 1) - 1, 0), offers.length);
            offers.splice(pos, 0, ex);
          });
        }
        finalSlides = offers;
      }

      // Auto-populated static offers the unified order didn't place explicitly still need
      // to render — e.g. the Static switch was just turned on, or a slider has ONLY static
      // slides with no saved order (legacy path). In the unified path the placed ones were
      // already removed from staticMap; here we append whatever is left when the switch is ON.
      if (autoStatic) {
        Object.keys(staticMap).forEach(function (k) { finalSlides.push(staticMap[k]); });
      }

      // Set default active offer type for each
      finalSlides.forEach((s, i) => {
        this.$set(this.activeOffer, i, 'lease');
        // Attach per-slide responsive visibility to OEM slides
        // (extras/static already carry hidden_on from their payload).
        if (s && !s.is_extra && !s.is_static && !Array.isArray(s.hidden_on)) {
          var sk = keyOf(s);
          s.hidden_on = (ss[sk] && ss[sk].hidden_on) || [];
        }
      });

      this.offersData = finalSlides;
      this.loading = false;
      if (this.isMotion) {
        var mdSelf = this;
        this.mdBoot = true;
        this.mdLoadJelly().then(function () {
          return mdSelf.motionTemplate === 'halloween' ? mdSelf.hwPrepareGlows() : null;
        }).then(function () {
          mdSelf.mdBoot = false;
          mdSelf.$nextTick(function () { mdSelf.scheduleInitSwiper(); });
        });
        return;
      }
      // SSR stays up until scheduleInitSwiper() lays out the carousel, then hides it —
      // avoids revealing un-Swipered slides between loading=false and Swiper init.
      this.$nextTick(() => this.scheduleInitSwiper());
    },

    initSwiper() {
      if (!this.$refs.swiperContainer || this.offersData.length === 0) return;

      var isMediaColumns = this.config.layout === 'media-columns';
      var transition = isMediaColumns ? 'slide' : (this.config.transition || 'slide');
      var isSingleSlideEffect = !isMediaColumns && (transition === 'fade' || transition === 'creative' || transition === 'flip');

      const modules = [A11y];
      const swiperConfig = {
        modules,
        slidesPerView: 1,
        spaceBetween: isMediaColumns ? 0 : (this.config.spaceBetween || 20),
        grabCursor: true,
        // Needed so Swiper adds .swiper-slide-visible; carousel multi-view overlay text uses it in SCSS
        watchSlidesProgress: true,
        a11y: {
          prevSlideMessage: this.lang === 'fr-CA' ? 'Diapositive précédente' : 'Previous slide',
          nextSlideMessage: this.lang === 'fr-CA' ? 'Diapositive suivante' : 'Next slide',
        },
      };

      var isFullWidth = this.config.layout === 'offers-template' || this.config.layout === 'split' || this.config.layout === 'oem-offer-hero' || this.config.layout === 'brand-tabs' || this.isMotion;

      // Effects that force single slide view
      if (isMediaColumns) {
        var mcSlideCount = this.offersData.length;
        var tCols = Math.min(mcSlideCount, Math.max(1, parseInt(this.config.mediaColumnsTablet, 10) || 2));
        var dCols = Math.min(mcSlideCount, Math.max(1, parseInt(this.config.mediaColumnsDesktop, 10) || parseInt(this.config.columns, 10) || 3));
        // Mobile (<1024): disable swiper, show all stacked via CSS
        swiperConfig.enabled = false;
        swiperConfig.slidesPerView = 1;
        swiperConfig.spaceBetween = 0;
        // Columns kick in at the (configurable) tablet breakpoint; default 1024 → unchanged.
        var mcBpT = this.bpTablet();
        swiperConfig.breakpoints = {};
        swiperConfig.breakpoints[mcBpT] = { enabled: true, slidesPerView: tCols };
        swiperConfig.breakpoints[mcBpT + 1] = { enabled: true, slidesPerView: dCols };
      } else if (isSingleSlideEffect || isFullWidth) {
        swiperConfig.slidesPerView = 1;
        swiperConfig.spaceBetween = 0;
      } else if (transition === 'coverflow') {
        swiperConfig.slidesPerView = 'auto';
        swiperConfig.spaceBetween = 0;
      } else {
        // Cap per-breakpoint columns to the actual slide count so a slider with
        // fewer slides than its configured column count doesn't leave empty space
        // (1 slide on a 3-col carousel was rendering at 1/3 width).
        var slideCount = this.offersData.length;
        var cMobile = Math.min(slideCount, Math.max(1, parseInt(this.config.carouselColumnsMobile, 10) || 1));
        var cTablet = Math.min(slideCount, Math.max(1, parseInt(this.config.carouselColumnsTablet, 10) || Math.min(2, parseInt(this.config.columns, 10) || 3)));
        var cDesktop = Math.min(slideCount, Math.max(1, parseInt(this.config.carouselColumnsDesktop, 10) || parseInt(this.config.columns, 10) || 3));
        swiperConfig.slidesPerView = cMobile;
        // Configurable cutoffs; defaults 640 / 1024 → identical to the previous hardcoded values.
        var cBpM = this.bpMobile(), cBpT = this.bpTablet();
        swiperConfig.breakpoints = {};
        swiperConfig.breakpoints[cBpM] = { slidesPerView: cTablet };
        swiperConfig.breakpoints[cBpT] = { slidesPerView: cDesktop };
      }

      // Transition effects
      if (transition === 'fade') {
        modules.push(EffectFade);
        swiperConfig.effect = 'fade';
        swiperConfig.fadeEffect = { crossFade: true };
      } else if (transition === 'creative') {
        modules.push(EffectCreative);
        swiperConfig.effect = 'creative';
        swiperConfig.creativeEffect = {
          perspective: true,
          prev: {
            shadow: true,
            translate: ['-125%', 0, -800],
            rotate: [0, -90, 0],
            opacity: 0,
          },
          next: {
            shadow: true,
            translate: ['125%', 0, -800],
            rotate: [0, 90, 0],
            opacity: 0,
          },
        };
        swiperConfig.speed = 800;
      } else if (transition === 'flip') {
        modules.push(EffectCreative);
        swiperConfig.effect = 'creative';
        swiperConfig.creativeEffect = {
          prev: {
            shadow: true,
            translate: [0, 0, -400],
            rotate: [0, 180, 0],
            opacity: 0,
          },
          next: {
            shadow: true,
            translate: [0, 0, -400],
            rotate: [0, -180, 0],
            opacity: 0,
          },
        };
        swiperConfig.speed = 700;
      } else if (transition === 'coverflow') {
        modules.push(EffectCoverflow);
        swiperConfig.effect = 'coverflow';
        swiperConfig.coverflowEffect = {
          rotate: 30,
          stretch: 0,
          depth: 200,
          modifier: 1,
          slideShadows: true,
        };
        swiperConfig.centeredSlides = true;
        swiperConfig.speed = 600;
      }

      if (this.config.showArrows && this.$refs.swiperPrev && this.$refs.swiperNext) {
        modules.push(Navigation);
        swiperConfig.navigation = {
          prevEl: this.$refs.swiperPrev,
          nextEl: this.$refs.swiperNext,
        };
      }

      if (this.config.showPagination && this.$refs.swiperPagination && !this.isBrandTabs) {
        modules.push(Pagination);
        swiperConfig.pagination = {
          el: this.$refs.swiperPagination,
          clickable: true,
        };
      }

      if (this.config.autoplay) {
        modules.push(Autoplay);
        swiperConfig.autoplay = {
          delay: this.isMotion ? Math.max(this.motionTemplate === 'halloween' ? 9000 : 4000, this.config.autoplaySpeed || 5000) : (this.config.autoplaySpeed || 5000),
          // Don't permanently disable autoplay when the user clicks/swipes — they can
          // navigate manually and autoplay keeps running. Hover still pauses and
          // resumes correctly via pauseOnMouseEnter.
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        };
      }

      // Infinite loop — slides cycle continuously. Requires enough real slides for loop to work.
      // Swiper auto-disables loop when only 1 slide; safe to enable always.
      if (this.config.loop && this.offersData.length > 1) {
        swiperConfig.loop = true;
        swiperConfig.loopAdditionalSlides = 2;
      }

      swiperConfig.modules = modules;

      var self = this;
      swiperConfig.on = {
        slideChange: function(swiper) {
          self.activeSlideIndex = swiper.realIndex;
          if (self.isMotion) self.motionPlay(swiper.slides[swiper.activeIndex]);
          // The popup lives outside the track, so a slide change would leave it
          // floating next to an icon that is no longer there.
          self.closeDisclaimer();
          // Pause/play videos on slide change. Layouts with multiple slides visible at once
          // (media-columns, multi-column carousel) should keep ALL visible videos playing,
          // not just the "active" one — otherwise off-center columns stay frozen.
          if (self.config.videoAutoplay && !self.config.videoHoverPlay) {
            var isMultiVisible = self.config.layout === 'media-columns'
              || (self.config.layout === 'carousel' && swiper.params && swiper.params.slidesPerView > 1);
            var allVids = swiper.el.querySelectorAll('.swiper-slide video');
            if (isMultiVisible) {
              // Play every video in the track.
              for (var i = 0; i < allVids.length; i++) {
                var p = allVids[i].play();
                if (p && typeof p.catch === 'function') p.catch(function(){});
              }
            } else {
              // Single-slide visible: pause all, play only the active one.
              for (var j = 0; j < allVids.length; j++) allVids[j].pause();
              var active = swiper.slides[swiper.activeIndex];
              if (active) {
                var av = active.querySelector('video');
                if (av) { var p2 = av.play(); if (p2 && typeof p2.catch === 'function') p2.catch(function(){}); }
              }
            }
          }
        },
      };

      this.swiperInstance = new Swiper(this.$refs.swiperContainer, swiperConfig);
      if (this.isMotion) this.motionStart();
    },

    /**
     * Schedule Swiper init to run outside the critical path. Swiper's first
     * update() cycle reads clientWidth/clientHeight on the container, iterates
     * each slide reading offsetLeft/offsetTop, and calls getComputedStyle
     * after style writes — combined that's ~180ms of forced reflow during
     * initial load (per Lighthouse forced-reflow insight). Deferring to idle
     * time (or the next frame as fallback) keeps these reads out of the first
     * long task, lowering TBT without changing the slider's visual behavior.
     */
    scheduleInitSwiper() {
      // Init SYNCHRONOUSLY here. Callers invoke this from inside a $nextTick, so the DOM
      // is already updated but the browser hasn't painted yet — laying out Swiper now means
      // it paints the finished carousel in one frame. Then we drop the SSR placeholder, so
      // the swap goes straight from "SSR" to "fully laid-out carousel" with no intermediate
      // flash of un-Swipered slides (the old requestIdleCallback deferred this up to 1s,
      // which is exactly the "one big image, then the rest snap into columns" jump).
      this.initSwiper();
      this.hideSSR();
    },

    // ─── Per-slide responsive visibility ───────────────────────────
    // A slide shows in the current bucket unless its hidden_on list contains it.
    isSlideInBucket(offer) {
      if (!offer) return false;
      var hidden = offer.hidden_on;
      if (!hidden || !hidden.length) return true;
      return hidden.indexOf(this.currentBucket) === -1;
    },
    isSlideVisibleInBucket(offer) {
      if (!this.isSlideInBucket(offer)) return false;
      return !this.isBrandTabs || this.btTabOf(offer) === this.btActive;
    },

    btTabOf(offer) {
      var tabs = this.btConfig.tabs || [];
      if (!tabs.length || !offer) return '';
      var i;
      var forced = offer.tab || '';
      if (!forced && this.isMinerva && !offer.is_extra && !offer.is_static) {
        forced = (this.slideOverride(offer) || {}).tab || '';
      }
      if (forced) {
        for (i = 0; i < tabs.length; i++) {
          if (tabs[i].id === forced) return forced;
        }
      }
      var raw = offer.is_static ? (offer.makes || []) : ((!offer.is_extra && offer.offers && offer.offers.make) ? [offer.offers.make] : []);
      var makes = [];
      for (i = 0; i < raw.length; i++) makes.push(String(raw[i]).toLowerCase());
      for (i = 0; i < tabs.length; i++) {
        if (tabs[i].make && makes.indexOf(tabs[i].make) !== -1) return tabs[i].id;
      }
      return tabs[0].id;
    },
    btFirstOfferOf(id) {
      for (var i = 0; i < this.offersData.length; i++) {
        var o = this.offersData[i];
        if (this.isSlideInBucket(o) && this.btTabOf(o) === id) return o;
      }
      return null;
    },
    btHasOverlayContent(offer) {
      if (!offer) return false;
      if (this.isMinerva && !offer.is_extra && !offer.is_static && !this.shouldHideOfferData(offer)) return true;
      return !!(this.heroTitle(offer) || this.slideCustomDesc(offer) || this.slideInlineCtas(offer).length);
    },
    btSelectTab(id) {
      if (!id || id === this.btActive || this.btFlipping) return;
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var from = this.btCurrentImage();
      var toOffer = this.btFirstOfferOf(id);
      var toUrl = toOffer ? this.heroImage(toOffer) : '';
      if (reduce || !this.$refs.btFlip || !from || !toUrl || this.hasCardVideo(toOffer) || this.hasEmbed(toOffer)) {
        this.btSwap(id);
        return;
      }
      var self = this;
      this.btFlipping = true;
      this.btPendingTab = id;
      this.$nextTick(function () { self.btMeasureRail(); });
      this.btPreload(toUrl).then(function () { self.btFlip(from, id, toUrl); });
    },
    btSwap(id) {
      this.closeDisclaimer();
      this.btActiveTab = id;
      this.btPendingTab = '';
      this.activeSlideIndex = 0;
      this.btCycle++;
      this.rebuildForBucket();
      var self = this;
      this.$nextTick(function () { self.btMeasureRail(); });
    },
    btCurrentImage() {
      var root = this.$refs.swiperContainer;
      var img = root && (root.querySelector('.swiper-slide-active .lbx-slider__hero-bg') || root.querySelector('.lbx-slider__hero-bg'));
      if (!img || !img.complete || !img.naturalWidth) return null;
      var cs = window.getComputedStyle(img);
      return { url: img.currentSrc || img.src, fit: cs.objectFit, position: cs.objectPosition };
    },
    btPreload(url) {
      return new Promise(function (resolve) {
        var img = new Image();
        var finish = function () { resolve(); };
        img.onload = function () {
          if (img.decode) img.decode().then(finish, finish); else finish();
        };
        img.onerror = finish;
        setTimeout(finish, 1500);
        img.src = url;
      });
    },
    btFlip(from, id, toUrl) {
      var self = this;
      var host = this.$refs.btFlip;
      var board = this.$refs.btBoard;
      var W = board ? board.clientWidth : 0;
      var H = board ? board.clientHeight : 0;
      if (!host || !W || !H) {
        this.btFlipping = false;
        this.btSwap(id);
        return;
      }
      var n = Math.max(6, Math.min(14, Math.round(W / 90)));
      var sw = W / n;
      var stagger = 45;
      var duration = 640;
      var cssUrl = function (u) { return 'url("' + String(u).replace(/["\\]/g, '\\$&') + '")'; };
      var bgSize = function (fit) {
        if (fit === 'contain' || fit === 'scale-down') return 'contain';
        if (fit === 'fill') return '100% 100%';
        if (fit === 'none') return 'auto';
        return 'cover';
      };
      while (host.firstChild) host.removeChild(host.firstChild);
      host.style.setProperty('--bt-apothem', (sw / (2 * Math.tan(Math.PI / 3))).toFixed(2) + 'px');
      host.style.setProperty('--bt-turn', duration + 'ms');
      host.style.perspective = Math.round(Math.max(900, W * 1.6)) + 'px';
      var incoming = [];
      for (var i = 0; i < n; i++) {
        var prism = document.createElement('div');
        prism.className = 'lbx-bt__prism';
        prism.style.left = (i * sw).toFixed(2) + 'px';
        prism.style.width = (sw + 1).toFixed(2) + 'px';
        prism.style.setProperty('--bt-delay', (i * stagger) + 'ms');
        ['a', 'b', 'c'].forEach(function (k) {
          var face = document.createElement('div');
          face.className = 'lbx-bt__face lbx-bt__face--' + k;
          if (k !== 'c') {
            var img = document.createElement('div');
            img.className = 'lbx-bt__face-img';
            img.style.width = W + 'px';
            img.style.height = H + 'px';
            img.style.left = (-i * sw).toFixed(2) + 'px';
            if (k === 'a') {
              img.style.backgroundImage = cssUrl(from.url);
              img.style.backgroundSize = bgSize(from.fit);
              img.style.backgroundPosition = from.position;
            } else {
              incoming.push(img);
            }
            face.appendChild(img);
          }
          prism.appendChild(face);
        });
        host.appendChild(prism);
      }
      board.style.height = H + 'px';
      host.classList.add('is-on');
      this.btSwap(id);
      this.$nextTick(function () {
        var root = self.$refs.swiperContainer;
        var next = root && (root.querySelector('.swiper-slide-active .lbx-slider__hero-bg') || root.querySelector('.swiper-slide:not(.swiper-slide-duplicate) .lbx-slider__hero-bg'));
        var cs = next ? window.getComputedStyle(next) : null;
        incoming.forEach(function (img) {
          img.style.backgroundImage = cssUrl(toUrl);
          img.style.backgroundSize = bgSize(cs ? cs.objectFit : 'cover');
          img.style.backgroundPosition = cs ? cs.objectPosition : 'center';
        });
        void host.offsetWidth;
        host.classList.add('is-turning');
        setTimeout(function () {
          host.classList.add('is-leaving');
          setTimeout(function () {
            host.classList.remove('is-on', 'is-turning', 'is-leaving');
            while (host.firstChild) host.removeChild(host.firstChild);
            board.style.height = '';
            self.btFlipping = false;
            self.btMeasureRail();
          }, 260);
        }, duration + (n - 1) * stagger + 40);
      });
    },
    btReadDealerColors() {
      var probe = this.$refs.btProbe;
      if (!probe || !window.getComputedStyle) return;
      var cs = window.getComputedStyle(probe);
      var solid = function (c) { return c && c !== 'transparent' && !/^rgba\(.*,\s*0\)$/.test(c) ? c : ''; };
      this.btDealer = { accent: solid(cs.color), fill: solid(cs.backgroundColor) };
    },
    mdReadDealerColors() {
      var probe = this.$refs.mdProbe;
      if (!probe || !window.getComputedStyle) return;
      var cs = window.getComputedStyle(probe);
      var solid = function (c) { return c && c !== 'transparent' && !/^rgba\(.*,\s*0\)$/.test(c) ? c : ''; };
      var accent = solid(cs.color);
      var fill = solid(cs.backgroundColor);
      this.mdDealer = {
        accent: accent,
        fill: fill,
        accentLight: this.mdLum(accent) > 0.2,
        fillLight: this.mdLum(fill) > 0.2,
        glow: this.mdTone(fill || accent, 0.42, true),
        night: this.mdTone(accent || fill, 0.025, false),
      };
    },
    mdRgb(color) {
      var m = /^rgba?\(/.test(color || '') ? color.match(/[\d.]+/g) : null;
      return m && m.length >= 3 ? [+m[0], +m[1], +m[2]] : null;
    },
    mdLum(color) {
      var rgb = typeof color === 'string' ? this.mdRgb(color) : color;
      if (!rgb) return 0;
      var lin = function (v) { v = v / 255; return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
      return 0.2126 * lin(rgb[0]) + 0.7152 * lin(rgb[1]) + 0.0722 * lin(rgb[2]);
    },
    mdTone(color, target, lighten) {
      var rgb = this.mdRgb(color);
      if (!rgb) return '';
      for (var t = 0; t <= 1.001; t += 0.05) {
        var c = rgb.map(function (v) { return lighten ? v + (255 - v) * t : v * (1 - t); });
        var lum = this.mdLum(c);
        if (lighten ? lum >= target : lum <= target) return 'rgb(' + c.map(Math.round).join(', ') + ')';
      }
      return lighten ? 'rgb(255, 255, 255)' : 'rgb(0, 0, 0)';
    },
    mdNorm(s) {
      return String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    },
    mdWords(s) {
      return String(s || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
    },
    mdHasScene(offer) {
      var url = ((offer.offers || {}).mainImage || {}).sourceUrl || '';
      return !!url && !/\.gif(\?|$)/i.test(url);
    },
    mdLoadJelly() {
      if (this._mdJellyP) return this._mdJellyP;
      var self = this;
      var makes = [];
      (this.offersData || []).forEach(function (o) {
        if (!self.isMotionOffer(o) || (self.motionTemplate !== 'kinetic' && self.motionTemplate !== 'halloween' && self.mdHasScene(o))) return;
        var make = o.offers.make;
        if (make && makes.indexOf(make) === -1) makes.push(make);
      });
      if (!makes.length || !window.fetch) {
        this._mdJellyP = Promise.resolve();
        return this._mdJellyP;
      }
      var thumb = this.motionTemplate === 'halloween' ? ' thumb: sourceUrl(size: THUMBNAIL)' : '';
      var query = '{' + makes.map(function (make, i) {
        return 'm' + i + ': modelLists(first: 100, where: { search: ' + JSON.stringify(String(make)) + ' }) { nodes { modelData { year make model jellybean { sourceUrl(size: LARGE)' + thumb + ' mediaDetails { width height } } } } }';
      }).join(' ') + '}';
      this._mdJellyP = new Promise(function (resolve) {
        var done = false;
        var finish = function () {
          if (done) return;
          done = true;
          resolve();
        };
        setTimeout(finish, 1800);
        fetch((self.minervaConfig || {}).graphqlEndpoint || 'https://minerva.stellate.sh/graphql', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query: query }),
        })
          .then(function (r) { return r.json(); })
          .then(function (json) { if (!done && json && json.data) self.mdJelly = self.mdJellyMap(json.data); })
          .catch(function () {})
          .then(finish);
      });
      return this._mdJellyP;
    },
    mdJellyMap(result) {
      var map = {};
      var self = this;
      Object.keys(result || {}).forEach(function (alias) {
        var nodes = (result[alias] && result[alias].nodes) || [];
        nodes.forEach(function (node) {
          var m = node && node.modelData;
          var j = m && m.jellybean;
          if (!m || !m.make || !m.model || !j || !j.sourceUrl) return;
          var d = j.mediaDetails || {};
          var key = self.mdNorm(m.make) + '|' + self.mdNorm(m.model);
          (map[key] = map[key] || []).push({
            url: j.sourceUrl,
            thumb: j.thumb || '',
            ratio: d.width && d.height ? d.width / d.height : 0,
            year: parseInt(m.year, 10) || 0,
            words: self.mdWords(m.model),
          });
        });
      });
      return map;
    },
    mdJellyFor(offer) {
      var map = this.mdJelly;
      var o = offer.offers || {};
      if (!map || !o.make || !o.model) return null;
      var make = this.mdNorm(o.make) + '|';
      var list = map[make + this.mdNorm(o.model)];
      if (!list) {
        var words = this.mdWords(o.model);
        var spare = Infinity;
        Object.keys(map).forEach(function (key) {
          if (key.indexOf(make) !== 0 || !words.length) return;
          var have = map[key][0].words;
          var fits = words.every(function (w) { return have.indexOf(w) !== -1; });
          if (fits && have.length - words.length < spare) {
            spare = have.length - words.length;
            list = map[key];
          }
        });
      }
      if (!list) return null;
      var year = parseInt(o.year, 10) || 0;
      var same = list.filter(function (it) { return it.year === year; })[0];
      return same || list.reduce(function (a, b) { return b.year > a.year ? b : a; });
    },
    mdFace(url) {
      var file = String(url || '').split('/').pop();
      return /[-_ ](FR|SR)[-_. ]/i.test(file) ? 'r' : 'l';
    },
    isMotionOffer(offer) {
      if (!this.isMotion || !this.isMinerva || !offer || offer.is_extra || offer.is_static || !offer.offers) return false;
      return !this.hasEmbed(offer) && !this.hasCardVideo(offer);
    },
    motionOf(index) {
      var m = this.motionItems[index];
      return m ? [m] : [];
    },
    motionBuild(offer) {
      var o = offer.offers || {};
      var tpl = this.motionTemplate;
      var ov = this.slideOverride(offer);
      var src = function (m) { return (m && m.sourceUrl) || ''; };
      var ratio = function (m) { var d = m && m.mediaDetails; return d && d.width && d.height ? d.width / d.height : 0; };
      var custom = ov.media_type_override === 'image' ? ((this.isSmallScreen && ov.image_url_mobile_override) || ov.image_url_override || '') : '';
      var hw = tpl === 'halloween';
      var big = this.mdHasScene(offer) ? src(o.mainImage) : '';
      var photo = custom || (big && ((this.isSmallScreen && src(o.mainImageMobile)) || big)) || '';
      var scene = tpl === 'kinetic' || hw ? '' : photo;
      var jelly = scene || (custom && (tpl === 'kinetic' || hw)) ? null : this.mdJellyFor(offer);
      if (hw && !jelly) scene = photo;
      var carMedia = src(o.desktopImage) ? o.desktopImage : o.mainImage;
      var card = scene || jelly ? '' : (custom || src(carMedia));
      var face = jelly ? this.mdFace(jelly.url) : 'l';
      var right = !hw && (scene ? this.slideAlignClass(offer) === 'lbx-align--right' : face === 'r');
      var head = [this.offerVal(offer, 'year'), this.offerVal(offer, 'make')].filter(Boolean).join(' ');
      var trim = this.offerVal(offer, 'trim');
      var model = this.offerVal(offer, 'model');
      var out = this.motionOffer(offer);
      var dial = tpl === 'ignition' ? this.motionDial(offer, out) : null;
      var ctas = this.motionCtas(offer);
      var cardRatio = custom ? 0 : ratio(carMedia);
      var wm = this.showWatermark(offer) ? 'lbx-motion--wm-' + this.config.watermarkPosition : '';
      var season = hw ? '' : this.motionSeason;
      return {
        tpl: tpl,
        classes: ['lbx-motion--' + tpl, 'lbx-motion--' + (scene ? 'scene' : (jelly ? 'cut' : 'card')), wm, season ? 'lbx-motion--season-' + season : '', { 'lbx-motion--fixed': !!this.config.sliderHeight, 'lbx-motion--copy-right': right }],
        season: season,
        kicker: season ? this.hwTitle.join(' ') : '',
        tape: season ? this.seasonTape : '',
        style: { '--md-dir': face === 'r' ? -1 : 1 },
        scene: scene,
        card: card,
        cardStyle: cardRatio ? { '--md-car': cardRatio } : {},
        cut: jelly ? jelly.url : '',
        cutStyle: jelly ? { '--md-car': jelly.ratio || 1.75, '--md-cut-url': 'url("' + jelly.url.replace(/"/g, '%22') + '")' } : {},
        eyebrow: trim ? head + ' · ' + trim : head,
        model: model,
        ghost: model ? [model, model, model, model].join('\u2003') + '\u2003' : '',
        offer: out,
        dial: dial,
        gauge: dial ? this.motionGauge(dial) : null,
        hw: hw ? { title: this.hwTitle, em: this.hwTitleEm, month: this.hwMonth, ransom: this.hwRansom, model: [head, model].filter(Boolean).join(' '), offer: this.hwOffer(out), glow: (jelly && this.hwGlows[jelly.url] && this.hwGlows[jelly.url].src) || '', light: this.hwLight(jelly) } : null,
        ctas: ctas,
        link: this.slideImageLink(offer) || (ctas[0] ? { url: ctas[0].url, target: ctas[0].target } : null),
      };
    },
    motionCtas(offer) {
      var own = this.slideCtas(offer).slice(0, 2).map(function (c) { return { label: c.label, url: c.url, target: c.target || '' }; });
      if (own.length) return own;
      var out = [];
      var shop = this.ohShopNow(offer);
      if (shop) out.push({ label: this.config.learnMoreLabel || 'Learn More', url: shop, target: '' });
      if (this.config.showViewInventory) out.push({ label: this.config.motionInventoryLabel || 'View Inventory', url: this.inventoryUrl(offer), target: '' });
      return out;
    },
    motionOffer(offer) {
      var pay = this.offerVal(offer, 'payment');
      var leaseApr = this.ohApr(offer, 'leaseApr');
      var financeApr = this.ohApr(offer, 'financeApr');
      var leaseLabel = this.offerVal(offer, 'leaseLabel', 'leaseLabelFr');
      var financeLabel = this.offerVal(offer, 'financeLabel', 'financeLabelFr');
      var leaseTerm = this.offerVal(offer, 'leaseDisclaimer', 'leaseDisclaimerFr');
      var financeTerm = this.offerVal(offer, 'financeDisclaimer', 'financeDisclaimerFr');
      var down = this.ohDownPayment(offer);
      var downAmt = this.offerVal(offer, 'downPayment');
      var details = [];
      var pods = [];
      var term = '';
      var out = null;
      if (pay) {
        out = { label: leaseLabel || financeLabel, value: this.ohMoney(pay), unit: [this.offerVal(offer, 'paymentLabel', 'paymentLabelFr'), this.ohPaymentTaxLabel(offer)].filter(Boolean).join(' ') };
        if (leaseApr) {
          details.push(this.t('At', 'À') + ' ' + leaseApr);
          pods.push(this.motionPod(this.t('APR', 'Taux'), leaseApr, 10));
        }
        if (down) details.push(down);
        term = leaseTerm || financeTerm;
        if (term) details.push(term);
      } else if (leaseApr) {
        out = { label: leaseLabel, value: leaseApr, unit: leaseTerm };
        if (down) details.push(down);
        term = leaseTerm;
      } else if (financeApr) {
        out = { label: financeLabel, value: financeApr, unit: financeTerm };
        term = financeTerm;
      } else if (this.ohHasRebate(offer)) {
        var dollar = function (txt) { return String(txt || '').replace(/[\uFE69\uFF04]/g, '$'); };
        out = { label: '', value: dollar(this.offerVal(offer, 'normalOfferLine1', 'normalOfferLine1Fr')), unit: dollar(this.offerVal(offer, 'normalOfferLine2', 'normalOfferLine2Fr')), text: true };
      }
      if (!out) return null;
      var months = this.motionMonths(term);
      if (months) pods.push({ kind: 'ring', label: this.t('Months', 'Mois'), value: String(months), frac: Math.min(1, months / 96).toFixed(3) });
      if (downAmt && !out.text) {
        pods.push({ kind: 'plain', label: this.offerVal(offer, 'downPaymentLabel', 'downPaymentLabelFr') || this.t('Down', 'Comptant'), value: this.ohMoney(downAmt), frac: '1' });
      }
      if (financeApr && out.value !== financeApr) {
        details.push([financeLabel, financeApr].filter(Boolean).join(' '));
        pods.push(this.motionPod(financeLabel || this.t('Finance', 'Financement'), financeApr, 10));
      }
      out.details = details;
      out.pods = pods.slice(0, 3);
      out.count = out.text ? null : this.motionCount(out.value);
      out.term = term;
      return out;
    },
    motionPod(label, value, max) {
      var c = this.motionCount(value);
      return { kind: 'ring', label: label, value: value, frac: (c ? Math.max(0, Math.min(1, c.value / max)) : 1).toFixed(3) };
    },
    motionPlain(html) {
      return String(html || '').replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    },
    motionMonths(term) {
      var m = this.motionPlain(term).match(/(\d{1,3})\s*(?:months?|mois|mos?)\b/i);
      return m ? parseInt(m[1], 10) : 0;
    },
    motionDial(offer, out) {
      if (!out) return null;
      if (!out.text) {
        var plainTerm = /^(?:(?:for|per month for|jusqu['’]\s?à|sur|pendant)\s+)?(?:up to\s+)?\d{1,3}\s*(?:months?|mois|mos?)\.?$/i;
        var term = this.motionPlain(out.term);
        var termIsUnit = !!out.term && out.term === out.unit;
        return {
          label: out.label,
          value: out.value,
          unit: termIsUnit ? '' : out.unit,
          count: out.count,
          note: term && !plainTerm.test(term) ? { title: '', body: out.term } : null,
        };
      }
      var src = this.motionPlain(out.value) + ' ' + this.motionPlain(out.unit);
      var note = { title: out.value, body: out.unit };
      var m = this.motionMoney(src);
      if (!m) {
        return { label: this.t('Model year', 'Année modèle'), value: String(this.offerVal(offer, 'year') || ''), unit: '', count: null, note: note };
      }
      var value = m[0].replace(/\$\s+/, '$').trim();
      var upto = src.slice(0, m.index).match(/(up\s+to|jusqu['’]\s?à)\s*$/i);
      return { label: upto ? upto[1] : '', value: value, unit: '', count: this.motionCount(value), note: note };
    },
    motionMoney(src) {
      return String(src || '').match(/\$\s?\d[\d,.\u00a0\u202f ]*\d|\$\s?\d|\d[\d,.\u00a0\u202f ]*\d\s?\$|\d\s?\$|\d+(?:[.,]\d+)?\s?%/);
    },
    hwOffer(out) {
      if (!out) return null;
      if (!out.text) {
        return {
          label: out.label,
          value: out.value,
          unit: out.unit,
          count: out.count,
          details: out.details.filter(function (d) { return d && d !== out.unit; }).join(' · '),
          text: false,
        };
      }
      var line = this.motionPlain(out.value);
      var rest = this.motionPlain(out.unit);
      var src = line;
      var m = this.motionMoney(line);
      if (!m) {
        src = rest;
        m = this.motionMoney(rest);
      }
      if (!m) return { label: '', value: out.value, unit: out.unit, count: null, details: '', text: true };
      var value = m[0].replace(/\$\s+/, '$').trim();
      var after = src.slice(m.index + m[0].length).trim();
      return {
        label: src.slice(0, m.index).trim(),
        value: value,
        unit: src === line ? (after || rest) : after,
        count: this.motionCount(value),
        details: src === line && after ? rest : '',
        text: false,
      };
    },
    hwFace(url) {
      var name = String(url || '').split('/').pop();
      var tag = name.match(/[-_](FR|FL|SR|SL)[-_]jell/i);
      if (tag) return { face: /R$/i.test(tag[1]) ? 'r' : 'l', side: /^S/i.test(tag[1]) };
      return { face: /^cc_/i.test(name) || /_\d{3}_01\./.test(name) ? 'l' : '', side: false };
    },
    hwLight(jelly) {
      var glow = jelly ? this.hwGlows[jelly.url] : null;
      if (glow) return glow.light;
      var face = jelly ? this.hwFace(jelly.url).face : '';
      return { '--gx': face === 'r' ? '78%' : (face === 'l' ? '22%' : '50%'), '--gy': '46%' };
    },
    hwPrepareGlows() {
      var self = this;
      var jobs = {};
      (this.offersData || []).forEach(function (o) {
        var jelly = self.isMotionOffer(o) ? self.mdJellyFor(o) : null;
        if (jelly && jelly.thumb) jobs[jelly.url] = jelly;
      });
      var urls = Object.keys(jobs);
      if (!urls.length || !window.URL || !document.createElement('canvas').toBlob) return Promise.resolve();
      var found = {};
      var work = Promise.all(urls.map(function (url) {
        return self.hwGlowFor(jobs[url]).then(function (glow) {
          if (glow) found[url] = glow;
        }, function () {});
      }));
      return Promise.race([work, new Promise(function (resolve) { setTimeout(resolve, 1500); })]).then(function () {
        self.hwGlows = Object.assign({}, found);
      });
    },
    hwGlowFor(jelly) {
      var self = this;
      return new Promise(function (resolve, reject) {
        var img = new Image();
        img.crossOrigin = 'anonymous';
        img.onerror = reject;
        img.onload = function () {
          var map = self.hwGlowMap(img, self.hwFace(jelly.url));
          if (!map) {
            resolve(null);
            return;
          }
          var light = { '--gx': (map.x * 100).toFixed(1) + '%', '--gy': (map.y * 100).toFixed(1) + '%' };
          if (map.lit < 0.0004) {
            resolve({ src: '', light: light });
            return;
          }
          map.canvas.toBlob(function (blob) {
            if (!blob) {
              resolve({ src: '', light: light });
              return;
            }
            var src = URL.createObjectURL(blob);
            (self._hwBlobs = self._hwBlobs || []).push(src);
            resolve({ src: src, light: light });
          }, 'image/png');
        };
        img.src = jelly.thumb;
      });
    },
    hwGlowMap(img, view) {
      var W = 400;
      var H = Math.max(8, Math.round(W * img.naturalHeight / img.naturalWidth));
      var cv = document.createElement('canvas');
      cv.width = W;
      cv.height = H;
      var ctx = cv.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0, W, H);
      var px;
      try {
        px = ctx.getImageData(0, 0, W, H).data;
      } catch (e) {
        return null;
      }
      var N = W * H;
      var A = new Float32Array(N);
      var L = new Float32Array(N);
      var S = new Float32Array(N);
      var hist = new Uint32Array(101);
      var solid = 0;
      var x0 = W;
      var x1 = -1;
      var y0 = H;
      var y1 = -1;
      var p, x, y, q;
      for (p = 0; p < N; p++) {
        var r = px[p * 4] / 255;
        var g = px[p * 4 + 1] / 255;
        var b = px[p * 4 + 2] / 255;
        var mx = Math.max(r, g, b);
        A[p] = px[p * 4 + 3] / 255;
        L[p] = 0.2126 * r + 0.7152 * g + 0.0722 * b;
        S[p] = mx > 0.04 ? (mx - Math.min(r, g, b)) / mx : 0;
        if (A[p] > 0.9) {
          hist[Math.round(L[p] * 100)]++;
          solid++;
        }
        if (A[p] > 0.5) {
          x = p % W;
          y = (p / W) | 0;
          x0 = Math.min(x0, x);
          x1 = Math.max(x1, x);
          y0 = Math.min(y0, y);
          y1 = Math.max(y1, y);
        }
      }
      if (x1 < 0 || !solid) return null;
      var seen = 0;
      var p90 = 1;
      for (q = 0; q <= 100; q++) {
        seen += hist[q];
        if (seen >= solid * 0.9) {
          p90 = q / 100;
          break;
        }
      }
      var t0 = Math.max(0.62, p90 + 0.04);
      var t1 = Math.min(1, t0 + 0.22);
      var bw = x1 - x0 + 1;
      var bh = y1 - y0 + 1;
      var table = function (f) {
        var I = new Float64Array((W + 1) * (H + 1));
        for (var ty = 0; ty < H; ty++) {
          var row = 0;
          for (var tx = 0; tx < W; tx++) {
            row += f(ty * W + tx);
            I[(ty + 1) * (W + 1) + tx + 1] = I[ty * (W + 1) + tx + 1] + row;
          }
        }
        return I;
      };
      var box = function (I, bx, by, rad) {
        var xa = Math.max(0, bx - rad);
        var xb = Math.min(W, bx + rad + 1);
        var ya = Math.max(0, by - rad);
        var yb = Math.min(H, by + rad + 1);
        return I[yb * (W + 1) + xb] - I[ya * (W + 1) + xb] - I[yb * (W + 1) + xa] + I[ya * (W + 1) + xa];
      };
      var ss = function (a, c, t) {
        t = Math.min(1, Math.max(0, (t - a) / (c - a)));
        return t * t * (3 - 2 * t);
      };
      var IA = table(function (k) { return A[k] > 0.9 ? 1 : 0; });
      var IL = table(function (k) { return A[k] > 0.9 ? L[k] : 0; });
      var IL2 = table(function (k) { return A[k] > 0.9 ? L[k] * L[k] : 0; });
      var rad = Math.max(3, Math.round(bw * 0.02));
      var lo = view.side ? 0.74 : 0.26;
      var hi = view.side ? 0.84 : 0.4;
      var E = new Float32Array(N);
      var vals = [];
      for (y = y0; y <= y1; y++) {
        for (x = x0; x <= x1; x++) {
          p = y * W + x;
          if (A[p] < 0.9) continue;
          var u = (x - x0) / bw;
          var v = (y - y0) / bh;
          var mu = view.face === 'r' ? ss(lo, hi, u) : (view.face === 'l' ? ss(lo, hi, 1 - u) : ss(0.03, 0.1, u) * ss(0.03, 0.1, 1 - u));
          var mask = mu * ss(0.2, 0.36, v) * (1 - ss(0.53, 0.63, v));
          if (mask <= 0) continue;
          var n7 = box(IA, x, y, rad);
          if (n7 < 4) continue;
          var con = L[p] - box(IL, x, y, rad) / n7;
          if (con <= 0) continue;
          var n2 = box(IA, x, y, 2);
          var m2 = box(IL, x, y, 2) / n2;
          var sd = Math.sqrt(Math.max(0, box(IL2, x, y, 2) / n2 - m2 * m2));
          var core = 0.6 * ss(0.965, 0.99, L[p]) * (1 - ss(0.03, 0.08, S[p]));
          var e = mask * ss(t0, t1, L[p]) * (1 - 0.8 * S[p]) * Math.max(ss(0.14, 0.42, con), core) * (0.2 + 0.8 * ss(0.05, 0.16, sd));
          if (e > 0.02) {
            E[p] = e;
            vals.push(e);
          }
        }
      }
      vals.sort(function (a, c) { return c - a; });
      var budget = 0.0035 * bw * bh;
      var run = 0;
      var th = vals.length ? vals[vals.length - 1] : 1;
      for (q = 0; q < vals.length; q++) {
        run += vals[q];
        if (run >= budget) {
          th = vals[q];
          break;
        }
      }
      var lit = 0;
      var gx = 0;
      var gy = 0;
      for (p = 0; p < N; p++) {
        if (!E[p]) continue;
        E[p] = Math.min(1, E[p] / 0.55) * ss(th * 0.55, th, E[p]);
        lit += E[p];
        gx += E[p] * (p % W);
        gy += E[p] * ((p / W) | 0);
      }
      var blur = function (src, br, passes) {
        var a = Float32Array.from(src);
        var tmp = new Float32Array(N);
        var span = 2 * br + 1;
        for (var it = 0; it < passes; it++) {
          for (var ry = 0; ry < H; ry++) {
            var acc = 0;
            for (var k = -br; k <= br; k++) acc += a[ry * W + Math.min(W - 1, Math.max(0, k))];
            for (var rx = 0; rx < W; rx++) {
              tmp[ry * W + rx] = acc / span;
              acc += a[ry * W + Math.min(W - 1, rx + br + 1)] - a[ry * W + Math.max(0, rx - br)];
            }
          }
          for (var cx = 0; cx < W; cx++) {
            var acc2 = 0;
            for (var k2 = -br; k2 <= br; k2++) acc2 += tmp[Math.min(H - 1, Math.max(0, k2)) * W + cx];
            for (var cy = 0; cy < H; cy++) {
              a[cy * W + cx] = acc2 / span;
              acc2 += tmp[Math.min(H - 1, cy + br + 1) * W + cx] - tmp[Math.max(0, cy - br) * W + cx];
            }
          }
        }
        return a;
      };
      var near = blur(E, 1, 1);
      var halo = blur(E, Math.max(2, Math.round(bw * 0.012)), 3);
      var wide = blur(E, Math.max(4, Math.round(bw * 0.035)), 3);
      var out = document.createElement('canvas');
      out.width = W;
      out.height = H;
      var octx = out.getContext('2d');
      var od = octx.createImageData(W, H);
      for (p = 0; p < N; p++) {
        var k3 = Math.min(1, near[p] * 0.75 + halo[p] * 1.6 + wide[p] * 2.6);
        if (k3 <= 0.004) continue;
        od.data[p * 4] = 255;
        od.data[p * 4 + 1] = 242;
        od.data[p * 4 + 2] = 218;
        od.data[p * 4 + 3] = Math.round(255 * k3);
      }
      octx.putImageData(od, 0, 0);
      var fx = view.face === 'r' ? 0.78 : (view.face === 'l' ? 0.22 : 0.5);
      var strong = lit / (bw * bh) >= 0.0004;
      return {
        canvas: out,
        lit: lit / (bw * bh),
        x: strong ? gx / lit / W : (x0 + fx * bw) / W,
        y: strong ? gy / lit / H : (y0 + 0.46 * bh) / H,
      };
    },
    hwStart(stage) {
      this.hwStop();
      var canvas = stage && stage.querySelector('.lbx-hw__smoke');
      if (!canvas || !canvas.getContext) return;
      var self = this;
      var eng = {
        stage: stage,
        canvas: canvas,
        ctx: canvas.getContext('2d'),
        parts: [],
        acc: {},
        t: 0,
        last: 0,
        raf: 0,
        fit: 0,
        dur: (parseFloat(window.getComputedStyle(stage).getPropertyValue('--md')) || 9) * 1000,
      };
      this._hw = eng;
      this.hwFit(eng);
      for (var i = 0; i < 16; i++) this.hwSpawn(eng, 'fog', true);
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.hwDraw(eng);
        return;
      }
      var tick = function (now) {
        if (self._hw !== eng) return;
        eng.raf = requestAnimationFrame(tick);
        var dt = eng.last ? Math.min(50, now - eng.last) : 16;
        eng.last = now;
        if (self.mdOff || self.mdHold) return;
        self.hwStep(eng, dt);
      };
      eng.raf = requestAnimationFrame(tick);
    },
    hwStop() {
      var eng = this._hw;
      if (!eng) return;
      cancelAnimationFrame(eng.raf);
      eng.ctx.setTransform(1, 0, 0, 1, 0, 0);
      eng.ctx.clearRect(0, 0, eng.canvas.width, eng.canvas.height);
      this._hw = null;
    },
    hwFit(eng) {
      var r = eng.canvas.getBoundingClientRect();
      var k = Math.min(window.devicePixelRatio || 1, 2) * 0.5;
      var w = Math.max(1, Math.round(r.width * k));
      var h = Math.max(1, Math.round(r.height * k));
      if (eng.canvas.width !== w || eng.canvas.height !== h) {
        eng.canvas.width = w;
        eng.canvas.height = h;
      }
      eng.k = k;
      eng.rect = r;
    },
    hwRect(eng, sel) {
      var el = eng.stage.querySelector(sel);
      if (!el) return null;
      var r = el.getBoundingClientRect();
      return { x: (r.left - eng.rect.left) * eng.k, y: (r.top - eng.rect.top) * eng.k, w: r.width * eng.k, h: r.height * eng.k };
    },
    hwStep(eng, dt) {
      eng.t += dt;
      eng.fit -= dt;
      if (eng.fit <= 0) {
        eng.fit = 400;
        this.hwFit(eng);
      }
      var p = eng.t / eng.dur;
      var rates = {
        fog: 2.6,
        creep: p < 0.4 ? 5 : 0,
        car: p > 0.24 && p < 0.45 ? 26 : 0,
        engulf: p > 0.36 && p < 0.46 ? 13 : 0,
        wisp: p > 0.5 && p < 0.7 ? 20 : 0,
      };
      var fog = 0;
      for (var i = 0; i < eng.parts.length; i++) {
        if (eng.parts[i].kind === 'fog') fog++;
      }
      if (fog > 26) rates.fog = 0;
      for (var kind in rates) {
        if (!rates[kind]) continue;
        eng.acc[kind] = (eng.acc[kind] || 0) + rates[kind] * dt / 1000;
        while (eng.acc[kind] >= 1) {
          eng.acc[kind] -= 1;
          if (eng.parts.length < 190) this.hwSpawn(eng, kind, false);
        }
      }
      var sec = dt / 1000;
      var cut = p > 0.46 ? Math.pow(0.02, dt / 800) : 1;
      for (var j = eng.parts.length - 1; j >= 0; j--) {
        var q = eng.parts[j];
        q.age += dt;
        if (cut < 1 && q.kind !== 'fog' && q.kind !== 'wisp') q.a *= cut;
        if (q.age >= q.life || q.a < 0.004) {
          eng.parts.splice(j, 1);
          continue;
        }
        q.x += q.vx * sec;
        q.y += q.vy * sec;
        q.r += q.vr * sec;
        q.rot += q.vrot * sec;
      }
      this.hwDraw(eng);
    },
    hwSpawn(eng, kind, settled) {
      var W = eng.canvas.width;
      var H = eng.canvas.height;
      var R = Math.random;
      var q = { kind: kind, age: 0, rot: R() * 6.283, vrot: (R() - 0.5) * 0.5, sprite: 'fog', add: false };
      if (kind === 'fog') {
        q.x = R() * W * 1.2 - W * 0.1;
        q.y = H * (0.8 + R() * 0.26);
        q.r = H * (0.16 + R() * 0.2);
        q.vr = H * 0.012;
        q.vx = (R() - 0.5) * W * 0.025;
        q.vy = -H * 0.004;
        q.life = 7000 + R() * 5000;
        q.a = 0.1 + R() * 0.1;
      } else if (kind === 'creep') {
        q.x = W * (0.62 + R() * 0.42);
        q.y = H * (0.62 + R() * 0.38);
        q.r = H * (0.12 + R() * 0.14);
        q.vr = H * 0.06;
        q.vx = -W * (0.02 + R() * 0.04);
        q.vy = -H * (0.01 + R() * 0.03);
        q.life = 3500 + R() * 2500;
        q.a = 0.12 + R() * 0.1;
        q.sprite = eng.t / eng.dur > 0.24 ? 'warm' : 'fog';
      } else if (kind === 'engulf') {
        q.x = W * (0.15 + R() * 0.7);
        q.y = H * (0.5 + R() * 0.45);
        q.r = H * (0.22 + R() * 0.22);
        q.vr = H * 0.3;
        q.vx = (R() - 0.5) * W * 0.04;
        q.vy = -H * 0.06;
        q.life = 1500 + R() * 900;
        q.a = 0.18 + R() * 0.16;
        q.sprite = R() < 0.5 ? 'warm' : 'fog';
      } else {
        var box = this.hwRect(eng, kind === 'car' ? '.lbx-hw__car' : '.lbx-hw__title') || { x: W * 0.35, y: H * 0.4, w: W * 0.3, h: H * 0.3 };
        if (kind === 'car') {
          q.x = box.x + box.w * (0.1 + R() * 0.8);
          q.y = box.y + box.h * (0.72 + R() * 0.28);
          q.r = box.h * (0.12 + R() * 0.16);
          q.vr = box.h * (0.3 + R() * 0.3);
          q.vx = (q.x - box.x - box.w / 2) * (0.5 + R() * 0.6) + (R() - 0.5) * W * 0.03;
          q.vy = -box.h * (0.15 + R() * 0.45);
          q.life = 2600 + R() * 1600;
          q.a = 0.13 + R() * 0.12;
          q.sprite = 'warm';
        } else {
          q.x = box.x + R() * box.w;
          q.y = box.y + box.h * (0.2 + R() * 0.8);
          q.r = box.h * (0.07 + R() * 0.12);
          q.vr = box.h * 0.2;
          q.vx = (R() - 0.5) * W * 0.02;
          q.vy = -H * (0.04 + R() * 0.08);
          q.life = 1100 + R() * 1100;
          q.a = 0.22 + R() * 0.2;
          q.sprite = 'blue';
          q.add = true;
        }
      }
      if (settled) q.age = q.life * (0.3 + R() * 0.4);
      eng.parts.push(q);
    },
    hwDraw(eng) {
      var ctx = eng.ctx;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, eng.canvas.width, eng.canvas.height);
      for (var i = 0; i < eng.parts.length; i++) {
        var q = eng.parts[i];
        var a = q.a * Math.sin(Math.PI * Math.min(1, q.age / q.life));
        if (a <= 0.003) continue;
        var c = Math.cos(q.rot);
        var s = Math.sin(q.rot);
        ctx.globalAlpha = a;
        ctx.globalCompositeOperation = q.add ? 'lighter' : 'source-over';
        ctx.setTransform(c, s, -s, c, q.x, q.y);
        ctx.drawImage(this.hwSprite(q.sprite), -q.r, -q.r, q.r * 2, q.r * 2);
      }
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
    },
    hwSprite(kind) {
      if (hwSprites[kind]) return hwSprites[kind];
      var c = document.createElement('canvas');
      c.width = 128;
      c.height = 128;
      var g = c.getContext('2d');
      var tint = { fog: '226, 234, 246', warm: '255, 238, 212', blue: '92, 162, 255' }[kind];
      var s = kind.length * 977 + 11;
      var rnd = function () { s = (s * 16807) % 2147483647; return (s - 1) / 2147483646; };
      for (var i = 0; i < 9; i++) {
        var x = 64 + (rnd() - 0.5) * 40;
        var y = 64 + (rnd() - 0.5) * 40;
        var r = 18 + rnd() * 22;
        var rg = g.createRadialGradient(x, y, 0, x, y, r);
        rg.addColorStop(0, 'rgba(' + tint + ', 0.42)');
        rg.addColorStop(1, 'rgba(' + tint + ', 0)');
        g.fillStyle = rg;
        g.beginPath();
        g.arc(x, y, r, 0, Math.PI * 2);
        g.fill();
      }
      hwSprites[kind] = c;
      return c;
    },
    motionGauge(dial) {
      var plain = dial ? this.motionPlain(dial.value) : '';
      var narrow = { '%': 0.88, '.': 0.3, ',': 0.3, ' ': 0.28, '\u00a0': 0.28, '\u202f': 0.28 };
      var em = 0;
      for (var i = 0; i < plain.length; i++) em += narrow[plain[i]] || 0.62;
      var vf = Math.min(0.2, 0.56 / Math.max(em, 1));
      var c = dial && dial.count;
      var max = 0;
      var ang = 135;
      if (c) {
        max = /%/.test(c.tpl) ? (c.value > 9 ? 20 : 10) : this.motionNiceMax(c.value * 1.5);
        ang = -135 + Math.min(1, c.value / max) * 270;
      }
      return { max: max, style: { '--md-ang': ang.toFixed(2) + 'deg', '--md-vf': vf } };
    },
    motionNiceMax(v) {
      if (!(v > 0)) return 10;
      var p = Math.pow(10, Math.floor(Math.log(v) / Math.LN10));
      var n = v / p;
      return (n <= 1 ? 1 : (n <= 2 ? 2 : (n <= 2.5 ? 2.5 : (n <= 5 ? 5 : 10)))) * p;
    },
    motionCount(value) {
      var s = String(value || '');
      if (!s || /<[^>]+>/.test(s)) return null;
      var m = s.match(/\d[\d.,\u00a0\u202f ]*\d|\d/);
      if (!m) return null;
      var norm = this.isFrench ? m[0].replace(/[\u00a0\u202f ]/g, '').replace(',', '.') : m[0].replace(/,/g, '');
      var num = parseFloat(norm);
      if (!isFinite(num)) return null;
      return { value: num, decimals: (norm.split('.')[1] || '').length, tpl: s.replace(m[0], '#') };
    },
    motionFormat(v, decimals) {
      var parts = v.toFixed(decimals).split('.');
      var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, this.isFrench ? '\u00a0' : ',');
      return parts[1] ? whole + (this.isFrench ? ',' : '.') + parts[1] : whole;
    },
    motionStart() {
      var sw = this.swiperInstance;
      var slide = sw && sw.slides[sw.activeIndex];
      var self = this;
      this.motionPlay(slide);
      var imgs = slide ? Array.prototype.slice.call(slide.querySelectorAll('.lbx-motion img')) : [];
      var loads = imgs.map(function (img) {
        if (img.complete) return Promise.resolve();
        return new Promise(function (resolve) {
          img.addEventListener('load', resolve, { once: true });
          img.addEventListener('error', resolve, { once: true });
        });
      });
      Promise.race([Promise.all(loads), new Promise(function (resolve) { setTimeout(resolve, 1500); })]).then(function () {
        self.mdHold = false;
        var ap = sw && sw.autoplay;
        if (self.config.autoplay && ap && ap.running) {
          ap.stop();
          ap.start();
        }
      });
    },
    motionPlay(slideEl) {
      if (!this.isMotion || !this.$el) return;
      var on = this.$el.querySelectorAll('.lbx-motion.is-playing');
      for (var i = 0; i < on.length; i++) on[i].classList.remove('is-playing');
      var stage = slideEl && slideEl.querySelector ? slideEl.querySelector('.lbx-motion') : null;
      if (!stage) {
        this.hwStop();
        return;
      }
      void stage.getBoundingClientRect();
      stage.classList.add('is-playing');
      this.mdMeasureWatermark();
      if (stage.classList.contains('lbx-motion--halloween')) this.hwStart(stage);
      else this.hwStop();
      var next = slideEl.nextElementSibling;
      var lazy = next ? next.querySelectorAll('.lbx-motion img[loading="lazy"]') : [];
      for (var j = 0; j < lazy.length; j++) lazy[j].loading = 'eager';
    },
    mdMeasureWatermark() {
      var wm = this.$el && this.$el.querySelector('.lbx-motion .lbx-slider__watermark');
      if (wm && wm.offsetHeight) this.$el.style.setProperty('--md-wm-h', wm.offsetHeight + 'px');
    },
    motionCountStart(e) {
      var el = e.target;
      if (!el || !el.getAttribute) return;
      if (e.animationName === 'lbx-md-gauge') {
        this.motionTrack(el);
        return;
      }
      if (e.animationName !== 'lbx-md-value') return;
      var target = parseFloat(el.getAttribute('data-md-count'));
      var tpl = el.getAttribute('data-md-tpl');
      if (!isFinite(target) || !tpl) return;
      var dec = parseInt(el.getAttribute('data-md-dec'), 10) || 0;
      var stage = el.closest('.lbx-motion');
      var md = stage ? parseFloat(window.getComputedStyle(stage).getPropertyValue('--md')) : 0;
      var dur = Math.max(700, (md || 6) * 180);
      var self = this;
      if (el._mdRaf) cancelAnimationFrame(el._mdRaf);
      else el._mdFinal = el.innerHTML;
      var t0 = null;
      var tick = function (t) {
        if (t0 === null) t0 = t;
        var p = Math.min(1, (t - t0) / dur);
        if (p < 1) {
          el.textContent = tpl.replace('#', self.motionFormat(target * (1 - Math.pow(1 - p, 3)), dec));
          el._mdRaf = requestAnimationFrame(tick);
        } else {
          el.innerHTML = el._mdFinal;
          el._mdRaf = null;
        }
      };
      el._mdRaf = requestAnimationFrame(tick);
    },
    motionTrack(gauge) {
      var v = gauge.querySelector('[data-md-count]');
      var max = parseFloat(gauge.getAttribute('data-md-max'));
      var tpl = v && v.getAttribute('data-md-tpl');
      if (!v || !tpl || !(max > 0)) return;
      var dec = parseInt(v.getAttribute('data-md-dec'), 10) || 0;
      var self = this;
      if (v._mdRaf) cancelAnimationFrame(v._mdRaf);
      else v._mdFinal = v.innerHTML;
      var stop = function (ev) {
        if (ev && ev.target !== gauge) return;
        cancelAnimationFrame(v._mdRaf);
        v._mdRaf = null;
        v.innerHTML = v._mdFinal;
        gauge.removeEventListener('animationend', stop);
        gauge.removeEventListener('animationcancel', stop);
      };
      gauge.addEventListener('animationend', stop);
      gauge.addEventListener('animationcancel', stop);
      var tick = function () {
        if (!gauge.closest('.is-playing')) {
          stop();
          return;
        }
        var a = parseFloat(window.getComputedStyle(gauge).getPropertyValue('--md-a'));
        if (isFinite(a)) v.textContent = tpl.replace('#', self.motionFormat(Math.max(0, (a + 135) / 270 * max), dec));
        v._mdRaf = requestAnimationFrame(tick);
      };
      v._mdRaf = requestAnimationFrame(tick);
    },
    btMeasureRail() {
      var totem = this.$refs.btTotem;
      var el = totem && totem.querySelector('.lbx-bt__tab.is-active');
      if (!el) { this.btRail = null; return; }
      var horizontal = window.getComputedStyle(totem).flexDirection.indexOf('row') === 0;
      this.btRail = horizontal
        ? { horizontal: true, x: el.offsetLeft, y: el.offsetTop + el.offsetHeight - totem.clientHeight, w: el.offsetWidth }
        : { horizontal: false, y: el.offsetTop, h: el.offsetHeight };
      if (horizontal && totem.scrollWidth > totem.clientWidth) {
        var left = Math.max(0, el.offsetLeft - (totem.clientWidth - el.offsetWidth) / 2);
        if (typeof totem.scrollTo === 'function') totem.scrollTo({ left: left, behavior: 'smooth' });
        else totem.scrollLeft = left;
      }
    },
    btTabKey(e, ti) {
      var step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
      var n = this.btTabs.length;
      var next = null;
      if (step) next = (ti + step + n) % n;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = n - 1;
      if (next === null) return;
      e.preventDefault();
      if (this.btFlipping) return;
      this.btSelectTab(this.btTabs[next].id);
      var btns = this.$refs.btTotem ? this.$refs.btTotem.querySelectorAll('.lbx-bt__tab') : [];
      if (btns[next]) btns[next].focus();
    },
    btSegClass(n) {
      return { 'is-done': n - 1 < this.activeSlideIndex, 'is-current': n - 1 === this.activeSlideIndex };
    },
    btGoTo(i) {
      var sw = this.swiperInstance;
      if (!sw) return;
      if (sw.params && sw.params.loop && typeof sw.slideToLoop === 'function') sw.slideToLoop(i);
      else sw.slideTo(i);
    },
    btHover(on) {
      if (!this.config.autoplay) return;
      this.btPaused = on;
      if (!on) this.btCycle++;
    },
    // Crossing a breakpoint changes which slides render (v-if). Wait for the DOM to
    // settle, then destroy + rebuild Swiper so its internal slide list matches.
    rebuildForBucket() {
      var self = this;
      this.$nextTick(function () {
        if (self.swiperInstance) {
          try { self.swiperInstance.destroy(true, true); } catch (e) {}
          self.swiperInstance = null;
        }
        self.scheduleInitSwiper();
      });
    },

    t(en, fr) {
      return this.isFrench ? (fr || en) : en;
    },

    isFinanceActive(index) {
      return this.activeOffer[index] === 'finance';
    },

    isLeaseActive(index) {
      return this.activeOffer[index] === 'lease';
    },

    showFinanceDetails(index, offer) {
      return this.isFinanceActive(index) && offer.offers.financeLabel;
    },

    // ─── Disclaimer popup (LBT-1460) ──────────────────────────
    // True when the popup for this slide index is currently open.
    isDisclaimerOpen(index) {
      return this.activeDisclaimer === index;
    },

    // Resolve disclaimer text for an offer using the locale priority:
    //   slide_settings override (per-offer) → Minerva GraphQL legalEn/legalFr → extra slide's own field → empty
    // Returns trimmed HTML string. Empty result means the icon should not render.
    disclaimerTextFor(offer) {
      if (!offer) return '';
      if (offer.is_static) return (offer.disclaimer || '').trim(); // static offers: their own disclaimer field
      var frLang = this.lang === 'fr-CA';
      // Extra slides: only their own disclaimer fields (no GraphQL fallback).
      if (offer.is_extra) {
        var exVal = frLang ? offer.disclaimer_fr : offer.disclaimer_en;
        return (exVal || '').trim();
      }
      // Minerva OEM: override beats GraphQL, GraphQL beats nothing.
      var ov = this.slideOverride ? this.slideOverride(offer) : {};
      var overrideVal = frLang ? (ov.disclaimer_fr || '') : (ov.disclaimer_en || '');
      if (overrideVal.trim()) return overrideVal.trim();
      var raw = offer.offers || offer;
      var legalVal = frLang ? (raw.legalFr || '') : (raw.legalEn || '');
      return (legalVal || '').trim();
    },

    // Icon visibility per slide. Two kill paths:
    //   1. Per-slide `hide_disclaimer` toggle wins outright — operator explicitly
    //      hid it for this slide, regardless of any legal text.
    //   2. No text resolves to anything to show.
    hasDisclaimerText(offer) {
      if (!offer) return false;
      // Static offers: just their own disclaimer text (no per-slide hide toggle, no override).
      if (offer.is_static) return !!this.disclaimerTextFor(offer);
      // Per-slide explicit hide — short-circuit before resolving any text.
      if (offer.is_extra) {
        if (offer.hide_disclaimer === true) return false;
      } else {
        var ov = this.slideOverride ? this.slideOverride(offer) : {};
        if (ov.hide_disclaimer === true) return false;
      }
      return !!this.disclaimerTextFor(offer);
    },

    // Hover open (desktop): show the popup while the cursor is over the icon
    // OR the popup itself (both live inside the wrapper that owns these handlers).
    // Skipped when a sticky (tap-opened) popup is already showing — letting the
    // hover preempt would close it on the way back out.
    hoverDisclaimer(index, e) {
      if (this.disclaimerSticky) return;
      // Re-entering the icon/popup cancels any pending close — this is what lets
      // the cursor travel across the gap into the popup without it vanishing.
      if (this._disclaimerCloseTimer) {
        clearTimeout(this._disclaimerCloseTimer);
        this._disclaimerCloseTimer = null;
      }
      this.activeDisclaimer = index;
      if (e) this.measureDisclaimer(e.currentTarget);
    },

    // Places the shared popup against the icon that opened it. Both rects are
    // viewport-relative, so subtracting them gives offsets inside the wrapper.
    measureDisclaimer(iconWrap) {
      if (!iconWrap || !this.$el) return;
      var host = this.$el.querySelector('.lbx-slider__wrapper');
      if (!host) return;
      var r = iconWrap.getBoundingClientRect();
      var h = host.getBoundingClientRect();
      var pos = this.config.disclaimerPosition || 'top-right';
      var gap = 10;
      var p = { placement: pos };
      if (pos === 'top-left' || pos === 'top-right') p.y = (r.bottom - h.top) + gap;
      else p.y = (h.bottom - r.top) + gap;
      if (pos === 'top-left' || pos === 'bottom-left') p.x = r.left - h.left;
      else p.x = h.right - r.right;
      this.disclaimerPos = p;
    },

    closeDisclaimer() {
      if (this._disclaimerCloseTimer) {
        clearTimeout(this._disclaimerCloseTimer);
        this._disclaimerCloseTimer = null;
      }
      this.activeDisclaimer = null;
      this.disclaimerSticky = false;
      this.disclaimerPos = null;
    },

    // Hover end (desktop): close UNLESS the popup was tap-opened (sticky).
    // We DON'T close immediately — there's a small gap between the icon and the
    // popup, and the wrapper reports mouseleave while the cursor crosses it.
    // A short grace period lets the cursor reach the popup; entering it fires
    // hoverDisclaimer again, which cancels this timer. Standard hover-intent.
    hoverDisclaimerEnd() {
      if (this.disclaimerSticky) return;
      if (this._disclaimerCloseTimer) clearTimeout(this._disclaimerCloseTimer);
      this._disclaimerCloseTimer = setTimeout(() => {
        this.activeDisclaimer = null;
        this.disclaimerPos = null;
        this._disclaimerCloseTimer = null;
      }, 220);
    },

    hasCta(cta) {
      return cta && cta.label && cta.url;
    },

    /**
     * Returns the full ctas array for a slide, resolving OEM overrides.
     * For Minerva OEM slides (not extra), reads from slideSettings.ctas (or legacy fields).
     * For custom/extra slides, reads from offer.ctas.
     */
    slideCtas(offer) {
      if (offer.is_static) return []; // static offers carry no CTAs — the image is the link
      if (this.isMinerva && !offer.is_extra) {
        var ov = this.slideOverride(offer);
        // New format: ctas array in slideSettings
        if (Array.isArray(ov.ctas) && ov.ctas.length > 0) {
          return ov.ctas.filter(function(c) { return c.label && c.url; });
        }
        // Legacy fields
        var result = [];
        if (ov.cta_label && ov.cta_url) result.push({ label: ov.cta_label, url: ov.cta_url, target: ov.cta_target || '_self', color: ov.cta_color || '', position: 'inline' });
        if (ov.cta2_label && ov.cta2_url) result.push({ label: ov.cta2_label, url: ov.cta2_url, target: '_self', color: '', position: 'inline' });
        return result;
      }
      // Custom / Extra / Countdown
      if (Array.isArray(offer.ctas)) return offer.ctas.filter(function(c) { return c.label && c.url; });
      // Legacy migration: build from main_cta / secondary_cta
      var legacyCtas = [];
      if (offer.main_cta && offer.main_cta.label && offer.main_cta.url) legacyCtas.push(Object.assign({ position: 'inline' }, offer.main_cta));
      if (offer.secondary_cta && offer.secondary_cta.label && offer.secondary_cta.url) legacyCtas.push(Object.assign({ position: 'inline' }, offer.secondary_cta));
      return legacyCtas;
    },

    /**
     * Returns CTAs that should render inline (position === 'inline').
     */
    slideInlineCtas(offer) {
      return this.slideCtas(offer).filter(function(c) { return !c.position || c.position === 'inline'; });
    },

    /**
     * Returns CTAs that should be absolutely positioned (position !== 'inline').
     */
    slideFloatingCtas(offer) {
      return this.slideCtas(offer).filter(function(c) { return c.position && c.position !== 'inline'; });
    },

    /**
     * Floating CTAs bundled by anchor. Each anchor used to position every one of
     * its buttons at the exact same coordinates, so a slide with three CTAs at
     * "bottom center" only ever showed the last one. Grouping lets the template
     * render one positioned box per anchor and lay its buttons out as a row —
     * the button-group look operators expect — while each button keeps its own
     * X/Y nudge.
     */
    floatingCtaGroups(offer) {
      var groups = [];
      var byPos = {};
      this.slideFloatingCtas(offer).forEach(function (c) {
        var pos = c.position;
        if (!byPos[pos]) {
          byPos[pos] = { position: pos, ctas: [] };
          groups.push(byPos[pos]);
        }
        byPos[pos].ctas.push(c);
      });
      return groups;
    },

    /**
     * Styles for an inline CTA. The secondary variant ships a 1px outline as
     * part of its default translucent look — but once the operator picks a
     * background colour the buttons are meant to match, and that leftover
     * outline is what showed up as "one button has a box around it".
     */
    inlineCtaStyle(cta) {
      var style = {};
      if (cta.color) {
        style.backgroundColor = cta.color;
        style.borderColor = 'transparent';
      }
      if (cta.text_color) style.color = cta.text_color;
      return Object.assign(style, this.ctaSizeStyle(cta), this.ctaLookStyle(cta));
    },

    ctaLookStyle(cta) {
      var style = {};
      var radius = { pill: '999px', rounded: '8px', square: '0' }[cta && cta.shape];
      if (radius) style.borderRadius = radius;
      if (cta && cta.variant === 'outline') {
        style.backgroundColor = 'transparent';
        style.borderWidth = '2px';
        style.borderStyle = 'solid';
        style.borderColor = cta.color || 'currentColor';
        if (cta.color && !cta.text_color) style.color = cta.color;
      }
      return Object.assign(style, this.weightStyle(cta && cta.weight));
    },

    weightStyle(weight) {
      if (weight === 'regular') return { fontFamily: '"Primary", system-ui, sans-serif', fontWeight: 400 };
      if (weight === 'bold') return { fontFamily: '"Primary Bold", "Primary", system-ui, sans-serif', fontWeight: 700 };
      return {};
    },

    countdownTextStyle(cfg) {
      var style = {};
      if (!cfg) return style;
      var size = this.fluidFontSize(cfg.size);
      if (size) style.fontSize = size;
      if (cfg.color) style.color = cfg.color;
      return Object.assign(style, this.weightStyle(cfg.weight));
    },

    /**
     * Per-CTA button size. One number (the desktop font size) drives the whole
     * button: the padding is expressed in em, so text and box grow together and
     * stay in proportion. Runs through the same clamp() as the headings, so a
     * chunky desktop button still shrinks sensibly on phones. Blank = untouched
     * theme default.
     */
    ctaSizeStyle(cta) {
      var size = this.fluidFontSize(cta && cta.size);
      if (!size) return {};
      // The CTA rows are flex with the default `align-items: stretch`, so every
      // button inherits the tallest one's height — a smaller size would change
      // the width but not the height. Opting just the sized button out of the
      // stretch keeps its real height, and leaves untouched buttons rendering
      // exactly as they do today.
      return { fontSize: size, padding: '0.6em 1.5em', alignSelf: 'center' };
    },

    /**
     * A floating button: its colours plus the admin's personal X/Y nudge, which
     * the CSS applies as a translate so it shifts the button inside its group
     * without breaking the group's own anchoring.
     */
    floatingCtaStyle(cta) {
      var style = {
        '--cta-dx': (parseInt(cta.offset_x, 10) || 0) + 'px',
        '--cta-dy': (parseInt(cta.offset_y, 10) || 0) + 'px',
      };
      if (cta.color) style.backgroundColor = cta.color;
      if (cta.text_color) style.color = cta.text_color;
      return Object.assign(style, this.ctaSizeStyle(cta), this.ctaLookStyle(cta));
    },

    hasSlideContent(offer) {
      return !!(this.heroTitle(offer) || this.heroDesc(offer) || this.hasAnyCta(offer));
    },

    /**
     * Per-slide alignment class. Falls back to global contentAlign.
     */
    slideAlignClass(offer) {
      // On small screens force EVERY slide to the LEFT, regardless of its chosen
      // alignment: right/center blocks get cut off and collide with the nav arrows on
      // narrow viewports. The left layout already reserves arrow safe-space and uses the
      // full width, so this keeps mobile clean & readable across all layouts.
      if (this.isSmallScreen) return 'lbx-align--left';
      var ss = this.slideSettings || {};
      if (this.isMinerva && offer.offers) {
        var o = offer.offers;
        var key = [(o.year || ''), (o.make || ''), (o.model || '')].filter(Boolean).join('_').toLowerCase().replace(/\s+/g, '-');
        if (ss[key] && ss[key].align) {
          return 'lbx-align--' + ss[key].align;
        }
        // Minerva per-offer default alignment (when no admin override is set for this slide).
        var ma = this.minervaAlign(o);
        if (ma) return 'lbx-align--' + ma;
      }
      if (!this.isMinerva && offer && offer.align) {
        return 'lbx-align--' + offer.align;
      }
      return this.contentAlignClass;
    },

    slideAlignValue(offer) {
      var ss = this.slideSettings || {};
      if (this.isMinerva && offer && offer.offers) {
        var o = offer.offers;
        var key = [(o.year || ''), (o.make || ''), (o.model || '')].filter(Boolean).join('_').toLowerCase().replace(/\s+/g, '-');
        if (ss[key] && ss[key].align) return ss[key].align;
        var ma = this.minervaAlign(o);
        if (ma) return ma;
      }
      if (!this.isMinerva && offer && offer.align) {
        return offer.align;
      }
      return this.config.contentAlign || 'left';
    },

    // Minerva per-offer content alignment (the dealer's per-slide default), normalized
    // to left/center/right; returns '' when absent or invalid so it falls through.
    minervaAlign(o) {
      var v = (o && o.contentAlignment != null) ? String(o.contentAlignment).trim().toLowerCase() : '';
      return (v === 'left' || v === 'center' || v === 'right') ? v : '';
    },

    heroBgPositionFor(offer) {
      var align = this.slideAlignValue(offer);
      if (align === 'right') return { objectPosition: 'left center' };
      if (align === 'center') return { objectPosition: 'center center' };
      return { objectPosition: 'right center' };
    },

    heroGradientFor(offer) {
      // Static offers are image-only promos — never tint them with the content gradient.
      if (offer && offer.is_static) return { background: 'transparent' };
      if (this.isBrandTabs && !this.btHasOverlayContent(offer)) return { background: 'transparent' };
      // Respect the per-slider "Show overlay" toggle — when off, no gradient is applied
      // so the full media is visible (ideal for logo carousels or clean hero shots).
      if (this.config.showOverlay === false) return { background: 'transparent' };
      var hex = (this.config.gradientColor || '#000032').replace('#', '');
      var r = parseInt(hex.substring(0, 2), 16) || 0;
      var g = parseInt(hex.substring(2, 4), 16) || 0;
      var b = parseInt(hex.substring(4, 6), 16) || 0;
      var rgb = r + ',' + g + ',' + b;
      var align = offer ? this.slideAlignValue(offer) : (this.config.contentAlign || 'left');
      var bg;
      if (align === 'right') {
        bg = 'linear-gradient(to left, rgba(' + rgb + ',0.85) 0%, rgba(' + rgb + ',0.65) 35%, rgba(' + rgb + ',0.1) 55%, transparent 70%)';
      } else if (align === 'center') {
        bg = 'radial-gradient(ellipse at center, rgba(' + rgb + ',0.85) 0%, rgba(' + rgb + ',0.55) 50%, rgba(' + rgb + ',0.15) 80%, transparent 100%)';
      } else {
        bg = 'linear-gradient(to right, rgba(' + rgb + ',0.85) 0%, rgba(' + rgb + ',0.65) 35%, rgba(' + rgb + ',0.1) 55%, transparent 70%)';
      }
      return { background: bg };
    },

    slideMainCta(offer) {
      var ctas = this.slideCtas(offer);
      return ctas.length > 0 ? ctas[0] : null;
    },

    slideSecondaryCta(offer) {
      var ctas = this.slideCtas(offer);
      return ctas.length > 1 ? ctas[1] : null;
    },

    slideImageLink(offer) {
      // Static offers: the whole image is a link (override or Minerva's own linkUrl).
      if (offer.is_static) {
        return offer.link_url ? { url: offer.link_url, target: offer.link_target || '_self' } : null;
      }
      // Custom / Extra / Countdown slides: direct property
      if (offer.image_link_url) {
        return { url: offer.image_link_url, target: offer.image_link_target || '_self' };
      }
      // OEM Minerva: check slideSettings override
      if (this.isMinerva && !offer.is_extra) {
        var ov = this.slideOverride(offer);
        if (ov.image_link_url) {
          return { url: ov.image_link_url, target: ov.image_link_target || '_self' };
        }
      }
      return null;
    },

    slideImageFitStyle(offer) {
      var fit = this.resolvedImageFit(offer);
      return { objectFit: fit };
    },

    /** Returns the effective object-fit value for a slide, resolving OEM overrides. */
    resolvedImageFit(offer) {
      // Slider-wide image fit: when the admin sets a general fit it FORCES every slide,
      // overriding each slide's own setting. Empty ('') means "per slide" (the chain below).
      if (this.config && this.config.imageFit) return this.config.imageFit;
      if (offer && offer.image_fit) return offer.image_fit;
      if (this.isMinerva && offer && !offer.is_extra) {
        var ov = this.slideOverride(offer);
        if (ov.image_fit) return ov.image_fit;
      }
      return 'cover';
    },

    /**
     * True when the slide container should adopt the image's natural aspect ratio
     * instead of a fixed height. Two triggers:
     *   1. fit=contain AND the admin left slider height empty → fluid on ALL screens
     *      (so letterbox bars don't appear around a contained image).
     *   2. fit=contain AND mobile AND no mobile-specific image → fluid on mobile only
     *      (legacy behavior to avoid letterboxing when admin didn't provide mobile art).
     */
    usesFluidHeight(offer) {
      if ((this.isBrandTabs || this.isMotion) && !this.config.sliderHeight) return true;
      if (this.resolvedImageFit(offer) !== 'contain') return false;
      // Case 1: admin didn't pin a height — use image's natural aspect ratio everywhere.
      if (!this.config.sliderHeight) return true;
      // Case 2: admin pinned a desktop height, but on mobile with no mobile-specific
      // image we still want fluid to avoid tall grey bars above/below.
      if (!this.isSmallScreen) return false;
      if (this.isMinerva && !offer.is_extra) {
        var ov = this.slideOverride(offer);
        if (ov.image_url_mobile_override) return false;
        var o = offer.offers || offer;
        if (o && o.mainImageMobile && o.mainImageMobile.sourceUrl) return false;
      }
      if (offer.image_mobile) return false;
      return true;
    },

    /** When the bg image loads, expose its natural aspect ratio via a CSS var on the slide container. */
    /**
     * True when more than one slide is on screen at a time.
     *
     * Those slides share a row, so letting each derive its own height from its own
     * image makes the row ragged — a strip of brand logos (ratios 1.0 to 7.4) came
     * out with cells from 45px to 188px tall. Read straight off Swiper so the
     * per-breakpoint column config doesn't have to be duplicated here.
     */
    sharesRowHeight() {
      var sw = this.swiperInstance;
      var per = (sw && sw.params) ? sw.params.slidesPerView : 1;
      if (per === 'auto') return true;
      return (parseFloat(per) || 1) > 1;
    },

    onSlideImageLoad(e) {
      if (this.isMotion) return;
      var self = this;
      var img = e && e.target ? e.target : null;
      if (!img || !img.naturalWidth || !img.naturalHeight) return;
      var ratio = img.naturalWidth / img.naturalHeight;
      // Walk up to the first slide container we recognize and stamp the ratio there.
      var container = img.closest('.lbx-offer-hero, .lbx-slider__hero, .lbx-slider__slide, .lbx-slider__split, .lbx-slider__media-col');
      if (!container) return;
      // Defer the style write to the next animation frame so it doesn't interleave
      // with Swiper's layout reads (clientWidth/clientHeight, offsetLeft/Top,
      // getComputedStyle) during init/update — keeps these out of the
      // forced-reflow critical path during the initial load.
      var raf = (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function')
        ? window.requestAnimationFrame
        : function (fn) { return setTimeout(fn, 16); };
      raf(function () {
        var root = img.closest('.lbx-bt, .lbx-slider');
        if (self.sharesRowHeight() || self.isBrandTabs) {
          // Shared row: every cell reads one ratio off the root. Slide 0 is the
          // reference (deterministic — lazy images finish loading out of order);
          // anything else only seeds it so the row isn't 16/9 in the meantime.
          if (container.style.getPropertyValue('--lbx-img-ratio')) {
            container.style.removeProperty('--lbx-img-ratio');
          }
          if (root && !(self.isBrandTabs && self.btRatio)) {
            var slide = img.closest('.swiper-slide');
            var isFirst = slide && !slide.classList.contains('swiper-slide-duplicate')
              && slide === slide.parentNode.querySelector('.swiper-slide:not(.swiper-slide-duplicate)');
            if (isFirst || !root.style.getPropertyValue('--lbx-img-ratio')) {
              root.style.setProperty('--lbx-img-ratio', ratio);
            }
          }
          return;
        }
        container.style.setProperty('--lbx-img-ratio', ratio);
        // Seed the slider ROOT with the first loaded image's ratio so slides that haven't
        // loaded yet (lazy) inherit a realistic aspect instead of the 16/9 fallback. Without
        // this, un-loaded slides default to 16/9 — if the real images are wider/shorter they
        // render taller, the Swiper wrapper grows to the tallest slide, and a gap appears
        // below the slider (arrows drop) until you navigate and they all load. Each slide
        // still overrides with its own ratio once it loads (more specific wins).
        if (root && !root.style.getPropertyValue('--lbx-img-ratio')) {
          root.style.setProperty('--lbx-img-ratio', ratio);
        }
      });
    },

    // Re-read every loaded bg image's natural aspect ratio and stamp it on its slide
    // container. Called on resize so contain/fluid slides recompute their height when the
    // viewport (and possibly the mobile↔desktop image) changes — no reload needed.
    refreshImageRatios() {
      if (this.isMotion || !this.$el || typeof this.$el.querySelectorAll !== 'function') return;
      var imgs = this.$el.querySelectorAll('.lbx-offer-hero__bg, .lbx-slider__hero-bg, .lbx-slider__slide-bg, .lbx-slider__split-img, .lbx-slider__media-col-bg');
      var shared = this.sharesRowHeight() || this.isBrandTabs;
      for (var i = 0; i < imgs.length; i++) {
        var img = imgs[i];
        if (!img.naturalWidth || !img.naturalHeight) continue;
        var c = img.closest('.lbx-offer-hero, .lbx-slider__hero, .lbx-slider__slide, .lbx-slider__split, .lbx-slider__media-col');
        if (!c) continue;
        // Shared row: drop any per-slide ratio so every cell keeps using the root's.
        if (shared) { c.style.removeProperty('--lbx-img-ratio'); continue; }
        c.style.setProperty('--lbx-img-ratio', img.naturalWidth / img.naturalHeight);
      }
    },

    showWatermark(offer) {
      if (!this.config.watermarkImage) return false;
      if (!offer) return false;
      if (offer.hide_watermark) return false;
      if (this.isMinerva && !offer.is_extra) {
        var ov = this.slideOverride(offer);
        if (ov.hide_watermark) return false;
      }
      return true;
    },

    hasAnyCta(offer) {
      if (this.isMinerva && !offer.is_extra && this.config.showViewInventory && !this.slideImageLink(offer)) return true;
      return this.slideCtas(offer).length > 0;
    },

    hasCardVideo(offer) {
      // Per-slide OEM override
      if (this.isMinerva && offer && !offer.is_extra) {
        var ov = this.slideOverride(offer);
        if (ov.media_type_override === 'video') return !!ov.video_url_override;
        if (ov.media_type_override === 'embed' || ov.media_type_override === 'image') return false;
      }
      return !!(offer && offer.video_url && offer.media_type === 'video');
    },

    /** Returns the video URL for the slide, considering per-OEM overrides. */
    slideVideoUrl(offer) {
      if (this.isMinerva && offer && !offer.is_extra) {
        var ov = this.slideOverride(offer);
        if (ov.media_type_override === 'video' && ov.video_url_override) return ov.video_url_override;
      }
      return offer && offer.video_url ? offer.video_url : '';
    },

    hasEmbed(offer) {
      // Per-slide OEM override
      if (this.isMinerva && offer && !offer.is_extra) {
        var ov = this.slideOverride(offer);
        if (ov.media_type_override === 'embed') return !!ov.embed_code_override;
        if (ov.media_type_override === 'video' || ov.media_type_override === 'image') return false;
      }
      return !!(offer && offer.media_type === 'embed' && offer.embed_code);
    },

    /**
     * Runs the <script> tags inside HTML-embed slides.
     *
     * The embed markup is injected with v-html (innerHTML), and browsers never
     * execute scripts inserted that way — so a third-party widget snippet just
     * sat there doing nothing while iframes worked fine. Re-creating each script
     * as a real element makes it run. The markup is admin-authored and stored raw
     * (same trust level as the per-slider Custom CSS), so this doesn't widen who
     * can inject code — it makes the Embed field behave as advertised.
     *
     * Idempotent: activated scripts are tagged so re-renders never re-run them.
     */
    activateEmbedScripts() {
      if (!this.$el || !this.$el.querySelectorAll) return;
      var boxes = this.$el.querySelectorAll('.lbx-slider__embed');
      for (var b = 0; b < boxes.length; b++) {
        var box = boxes[b];
        // Swiper's loop mode clones slides; running a widget once per clone would
        // load it several times. Only real slides get activated (cloned <script>
        // nodes never execute on their own, so the clones stay inert).
        if (box.closest && box.closest('.swiper-slide-duplicate')) continue;
        var scripts = box.querySelectorAll('script:not([data-lbx-embed-run])');
        for (var i = 0; i < scripts.length; i++) {
          var old = scripts[i];
          var fresh = document.createElement('script');
          for (var a = 0; a < old.attributes.length; a++) {
            fresh.setAttribute(old.attributes[a].name, old.attributes[a].value);
          }
          fresh.setAttribute('data-lbx-embed-run', '1');
          // Inline scripts carry their code; external ones run off their src.
          if (!old.src) fresh.text = old.textContent || '';
          old.parentNode.replaceChild(fresh, old);
        }
      }
    },

    /** Returns the embed HTML for the slide, considering per-OEM overrides. */
    slideEmbedCode(offer) {
      if (this.isMinerva && offer && !offer.is_extra) {
        var ov = this.slideOverride(offer);
        if (ov.media_type_override === 'embed' && ov.embed_code_override) return ov.embed_code_override;
      }
      return offer && offer.embed_code ? offer.embed_code : '';
    },

    /**
     * Best-available poster image for a video, shown while the video loads.
     * Priority: per-slide image_url_override > original OEM image > extra slide image > custom slide image.
     */
    videoPoster(offer) {
      if (!offer) return '';
      var useMobile = this.isSmallScreen;
      // OEM with video override: pick the best available poster, preferring mobile variants on small screens.
      if (this.isMinerva && !offer.is_extra) {
        var ov = this.slideOverride(offer);
        if (useMobile && ov.image_url_mobile_override) return ov.image_url_mobile_override;
        if (ov.image_url_override) return ov.image_url_override;
        var o = offer.offers || offer;
        var desktop = (o.mainImage && o.mainImage.sourceUrl) || (o.desktopImage && o.desktopImage.sourceUrl) || '';
        var mobileImg = (o.mainImageMobile && o.mainImageMobile.sourceUrl) || '';
        if (useMobile && mobileImg) return mobileImg;
        return desktop || '';
      }
      // Extra/custom slides: prefer mobile variant if set and on mobile.
      if (useMobile && offer.image_mobile) return offer.image_mobile;
      return offer.image || '';
    },

    // ─── Configurable breakpoints (per slider) ─────────────────────
    // The admin can move the mobile/tablet cutoffs. Defaults match the CSS (640 / 1024).
    // These drive Swiper's column breakpoints, the responsive-visibility buckets and the
    // mobile/desktop behavior — NOT the static SCSS media queries.
    bpMobile() {
      var v = parseInt(this.config && this.config.breakpointMobile, 10);
      return (v && v > 0) ? v : 640;
    },
    bpTablet() {
      var v = parseInt(this.config && this.config.breakpointTablet, 10);
      return (v && v > 0) ? v : 1024;
    },
    bucketForWidth(w) {
      if (w <= this.bpMobile()) return 'mobile';
      if (w < this.bpTablet()) return 'tablet';
      return 'desktop';
    },

    isMobileViewport() {
      if (typeof window === 'undefined') return false;
      return window.innerWidth < this.bpTablet();
    },

    shouldAutoplayVideo() {
      // Mobile (<1024): always autoplay
      if (this.isMobileViewport()) return true;
      return this.config.videoAutoplay && !this.config.videoHoverPlay;
    },

    supportsHoverPlayback() {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
      return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    },

    onMediaCardEnter(offer, e) {
      // No hover play on mobile — videos autoplay there
      if (this.isMobileViewport()) return;
      if (!this.config.videoHoverPlay || !this.hasCardVideo(offer) || !this.supportsHoverPlayback()) return;
      var video = e && e.currentTarget ? e.currentTarget.querySelector('video') : null;
      if (!video) return;
      var p = video.play();
      if (p && typeof p.catch === 'function') p.catch(function () {});
    },

    onMediaCardLeave(offer, e) {
      if (this.isMobileViewport()) return;
      if (!this.config.videoHoverPlay || !this.hasCardVideo(offer) || !this.supportsHoverPlayback()) return;
      var video = e && e.currentTarget ? e.currentTarget.querySelector('video') : null;
      if (!video) return;
      video.pause();
    },

    syncVideoPlayback() {
      var el = this.$el;
      if (!el) return;
      var videos = el.querySelectorAll('video');
      var mobile = this.isMobileViewport();
      for (var i = 0; i < videos.length; i++) {
        var v = videos[i];
        v.muted = true;
        if (mobile) {
          var p = v.play();
          if (p && typeof p.catch === 'function') p.catch(function(){});
        } else if (this.config.videoHoverPlay && this.supportsHoverPlayback()) {
          v.pause();
        } else if (!this.config.videoAutoplay) {
          v.pause();
        } else {
          var p2 = v.play();
          if (p2 && typeof p2.catch === 'function') p2.catch(function(){});
        }
      }
    },

    onBgVideoLoadStart(e) {
      var video = e && e.target ? e.target : null;
      if (video) video.classList.add('is-loading');
    },

    onBgVideoError(e) {
      // If the video fails to load (404, CORS, wrong MIME), remove the loading shimmer
      // and keep the poster image visible as a graceful fallback.
      var video = e && e.target ? e.target : null;
      if (!video) return;
      video.classList.remove('is-loading');
      // Intentionally do NOT add .is-ready — video stays at opacity 0, poster remains visible.
    },

    onBgVideoLoaded(e) {
      var video = e && e.target ? e.target : null;
      if (!video) return;
      // Remove the loading skeleton + fade the video in (poster crossfades out via .is-ready on its sibling).
      video.classList.remove('is-loading');
      video.classList.add('is-ready');
      // Fade the sibling poster (<img class="lbx-slider__video-poster">) out for a smooth crossfade.
      var parent = video.parentNode;
      if (parent) {
        var poster = parent.querySelector('.lbx-slider__video-poster');
        if (poster) poster.classList.add('is-hidden');
      }
      video.muted = true;
      try { video.currentTime = 0; } catch (err) {}
      // Mobile: always autoplay
      if (this.isMobileViewport()) return;
      // Desktop: respect settings
      if (this.config.videoHoverPlay && this.supportsHoverPlayback()) {
        video.pause();
      } else if (!this.config.videoAutoplay) {
        video.pause();
      }
    },

    /**
     * Hero layout helpers — normalize Minerva vs Manual/Custom offer shapes
     */
    inventoryUrl(offer) {
      var base = this.config.searchPath || '/search';
      // Static / Additional slides have no Minerva node — fall back to the base
      // search URL instead of crashing on offer.offers.year.
      var o = offer && offer.offers;
      if (!o) return base;
      var year = o.year || '';
      var make = (o.make || '').replace(/ /g, '_');
      var model = (o.model || '').replace(/ /g, '_');
      return base + '/filter/' + year + '_' + make + '_' + model + '/';
    },

    slideOverride(offer) {
      var ss = this.slideSettings || {};
      if (this.isMinerva && offer && !offer.is_extra && !offer.is_static) {
        // Offers may be wrapped in .offers (GraphQL node) or direct on the offer object
        var o = offer.offers || offer;
        var key = [(o.year || ''), (o.make || ''), (o.model || '')].filter(Boolean).join('_').toLowerCase().replace(/\s+/g, '-');
        return ss[key] || {};
      }
      return {};
    },

    heroImage(offer) {
      var useMobile = this.isSmallScreen;
      // Static offers are image-only — desktop/mobile straight from Minerva.
      if (offer.is_static) {
        if (useMobile && offer.image_mobile) return offer.image_mobile;
        return offer.image || '';
      }
      // Per-slide media override (OEM only): if user selected a custom image, use it.
      // Prefer the mobile override when on a small screen and it's set; otherwise fall back to desktop override.
      if (this.isMinerva && !offer.is_extra) {
        var ov = this.slideOverride(offer);
        if (ov.media_type_override === 'image') {
          if (useMobile && ov.image_url_mobile_override) return ov.image_url_mobile_override;
          if (ov.image_url_override) return ov.image_url_override;
        }
        // If override is video/embed, the bg-image is suppressed (handled by hasCardVideo/hasEmbed)
        if (ov.media_type_override === 'video' || ov.media_type_override === 'embed') return '';
      }
      // Extra slides (OEM) with optional mobile override.
      if (offer.is_extra) {
        if (useMobile && offer.image_mobile) return offer.image_mobile;
        return offer.image || '';
      }
      // Pure Minerva OEM (no overrides) — use Minerva's mobile/desktop fields.
      if (this.isMinerva) {
        var o = offer.offers || {};
        var desktop = (o.mainImage && o.mainImage.sourceUrl) || (o.desktopImage && o.desktopImage.sourceUrl) || '';
        var mobileImg = (o.mainImageMobile && o.mainImageMobile.sourceUrl) || '';
        if (useMobile && mobileImg) return mobileImg;
        return desktop;
      }
      // Custom slides (non-Minerva) — prefer mobile variant when available.
      if (useMobile && offer.image_mobile) return offer.image_mobile;
      return offer.image || '';
    },

    heroPicture(offer) {
      var src = offer ? this.heroImage(offer) : '';
      if (src && offer.pic_mobile && src === offer.image_mobile) return offer.pic_mobile;
      if (src && offer.pic && src === offer.image) return offer.pic;
      var o = src && offer.offers;
      if (o) {
        var media = [o.mainImage, o.mainImageMobile, o.desktopImage].filter(function (m) { return m && m.sourceUrl === src; })[0];
        var d = media && media.mediaDetails;
        if (d && d.width && d.height) return { w: d.width, h: d.height };
      }
      return {};
    },

    /**
     * True when all OEM offer text should be suppressed on this slide
     * (image stays, CTAs stay). Global toggle wins; per-slide override can
     * still turn it on even if global is off. Only applies to Minerva slides.
     */
    shouldHideOfferData(offer) {
      if (!offer || offer.is_extra || offer.is_static) return false;
      if (!this.isMinerva) return false;
      if (this.config.hideOfferData) return true;
      var ov = this.slideOverride(offer);
      return !!ov.hide_offer_data;
    },

    /**
     * True when the slide's offer text (APRs, payment, description/offer lines)
     * should render inside rounded pill containers with a semi-transparent bg.
     * OEM-only; global OR per-slide enables it.
     */
    useBoxedText(offer) {
      if (!offer || offer.is_extra || offer.is_static) return false;
      if (!this.isMinerva) return false;
      if (this.config.boxedOfferText) return true;
      var ov = this.slideOverride(offer);
      return !!ov.boxed_offer_text;
    },

    heroTitle(offer) {
      if (offer.is_static) return ''; // image-only — no overlay title
      if (offer.is_extra) return offer.title || '';
      if (this.isMinerva) {
        if (this.shouldHideOfferData(offer)) return '';
        var ov = this.slideOverride(offer);
        var ovTitle = this.isFrench ? (ov.title_fr || ov.title) : ov.title;
        if (ovTitle) return ovTitle;
        return offer.offers.year + ' ' + offer.offers.model + ' ' + (offer.offers.trim || '');
      }
      return offer.title || '';
    },

    heroAlt(offer) {
      if (!offer) return '';
      if (offer.is_static) return this.plainText(offer.alt || offer.title || '');
      if (offer.is_extra) return this.plainText(offer.title || '');
      if (this.isMinerva) {
        var ov = this.slideOverride(offer);
        var ovTitle = this.isFrench ? (ov.title_fr || ov.title) : ov.title;
        if (ovTitle) return this.plainText(ovTitle);
        var o = offer.offers || {};
        return this.plainText([o.year, o.model, o.trim].filter(Boolean).join(' '));
      }
      return this.plainText(offer.title || '');
    },

    /**
     * Turns an admin-picked DESKTOP px size into a responsive clamp() so a custom
     * heading size still scales down on phones (a bare px would override every
     * responsive rule and blow up on small screens). The vw term hits exactly the
     * chosen size at a 1440px viewport; the floor is half of it.
     */
    fluidFontSize(px) {
      var max = parseInt(px, 10) || 0;
      if (!max) return '';
      // Floor: 45% of the chosen size, but never below 15px so small sizes stay
      // readable on phones (and never above the chosen size for tiny values).
      var min = Math.max(Math.min(max, 15), Math.round(max * 0.45));
      return 'clamp(' + min + 'px, ' + (max / 14.4).toFixed(2) + 'vw, ' + max + 'px)';
    },

    heroTitleStyle(offer) {
      var style = {};
      // Additional slides carry their own brand styling (color + size), which
      // applies in every layout — including inside Minerva-sourced sliders.
      if (offer && offer.is_extra) {
        if (offer.title_color) style.color = offer.title_color;
        var exSize = this.fluidFontSize(offer.title_size);
        if (exSize) style.fontSize = exSize;
        return style;
      }
      if (this.isMinerva) return {};
      if (offer.title_color) style.color = offer.title_color;
      if (offer.title_bold) style.fontWeight = '700';
      if (offer.title_italic) style.fontStyle = 'italic';
      return style;
    },

    heroDescStyle(offer) {
      var style = {};
      if (offer && offer.is_extra) {
        if (offer.desc_color) style.color = offer.desc_color;
        var exSize = this.fluidFontSize(offer.desc_size);
        if (exSize) style.fontSize = exSize;
        return style;
      }
      if (this.isMinerva) return {};
      if (offer.desc_color) style.color = offer.desc_color;
      return style;
    },

    // Get overridden or original value for a Minerva offer field
    offerVal(offer, field, fieldFr) {
      // Suppress every OEM data lookup when the hide-offer-data toggle is on.
      // The template uses offerVal() inside v-if guards, so returning '' here
      // collapses titles, APRs, payment, offer lines, and FR counterparts.
      if (this.shouldHideOfferData(offer)) return '';
      var ov = this.slideOverride(offer);
      var o = (offer.offers || offer);
      if (fieldFr) {
        var ovVal = this.isFrench ? (ov[fieldFr] || ov[field]) : ov[field];
        if (ovVal !== undefined && ovVal !== '') return ovVal;
        return this.t(o[field], o[fieldFr]);
      }
      if (ov[field] !== undefined && ov[field] !== '') return ov[field];
      return o[field] || '';
    },

    /**
     * Format a numeric value with a suffix (e.g. "%"), but only if the user
     * provided plain text. If the value already contains HTML markup, return it
     * untouched so the user controls everything — including the suffix —
     * without us appending a stray "%" outside their styled span.
     *
     * Detection: any "<…>" sequence flags HTML mode.
     */
    formatRateValue(val, suffix) {
      if (val === null || val === undefined || val === '') return '';
      val = String(val);
      if (/<[^>]+>/.test(val)) return val;
      return val + (suffix || '');
    },

    // ─── OEM Offer Hero layout — the conditional "puzzle" ──────────────────
    // Each block shows only when its data is present, mirroring the mockups.
    // APR formatter: preserves a 0 ("0%") — offerVal()'s `|| ''` drops a numeric 0 —
    // and always appends the "%" sign (unless the value already has one / is HTML).
    // Minerva sends a 0% APR as null, so when the value is empty BUT the block
    // otherwise has its label/disclaimer (i.e. there IS a lease/finance offer here),
    // we infer a 0% rate. A block with no label and no disclaimer has no rate → ''.
    ohApr(offer, field) {
      if (!offer || this.shouldHideOfferData(offer)) return '';
      var o = offer.offers || offer;
      var ov = this.slideOverride ? this.slideOverride(offer) : {};
      var v = (ov[field] !== undefined && ov[field] !== '') ? ov[field] : (o ? o[field] : '');
      if (v === '' || v === null || v === undefined) {
        var prefix = field === 'leaseApr' ? 'lease' : (field === 'financeApr' ? 'finance' : '');
        if (prefix) {
          var hasLabel = !!this.offerVal(offer, prefix + 'Label', prefix + 'LabelFr');
          var hasDisc  = !!this.offerVal(offer, prefix + 'Disclaimer', prefix + 'DisclaimerFr');
          if (hasLabel || hasDisc) return '0%';
        }
        return '';
      }
      var str = String(v);
      if (/<[^>]+>/.test(str)) return str;
      // French uses a comma decimal separator (8.29% → 8,29%).
      if (str.indexOf('%') !== -1) return this.frNum(str);
      return this.frNum(str) + '%';
    },
    ohHasFinance(offer) {
      return !!(this.ohApr(offer, 'financeApr') || this.offerVal(offer, 'financeLabel', 'financeLabelFr'));
    },
    ohHasLease(offer) {
      return !!(this.offerVal(offer, 'leaseLabel', 'leaseLabelFr') || this.offerVal(offer, 'payment') || this.ohApr(offer, 'leaseApr'));
    },
    ohHasRebate(offer) {
      return !!(this.offerVal(offer, 'normalOfferLine1', 'normalOfferLine1Fr') || this.offerVal(offer, 'normalOfferLine2', 'normalOfferLine2Fr'));
    },
    // Down payment shown as "$X,XXX down" (value + its label).
    ohDownPayment(offer) {
      var amount = this.offerVal(offer, 'downPayment');
      if (!amount) return '';
      var label = this.offerVal(offer, 'downPaymentLabel', 'downPaymentLabelFr');
      amount = this.ohMoney(amount);
      return label ? (amount + ' ' + label) : amount;
    },
    // year + model + trim joined — used for the image alt text.
    ohTitle(offer) {
      return [this.offerVal(offer, 'year'), this.offerVal(offer, 'model'), this.offerVal(offer, 'trim')].filter(Boolean).join(' ');
    },
    // ── Fields that come from Minerva — wired to the GraphQL query once those
    //    fields exist there. Until then offerVal() returns '' and they no-op. ──
    ohShopNow(offer) {
      // French locale uses shopNowUrlFr when present; otherwise the default shopNowUrl.
      return this.offerVal(offer, 'shopNowUrl', 'shopNowUrlFr'); // Shop Now destination URL
    },
    ohPaymentTaxLabel(offer) {
      return this.offerVal(offer, 'paymentTaxLabel', 'paymentTaxLabelFr'); // e.g. "+ HST"
    },
    ohLogo(offer) {
      if (!offer) return '';
      var o = offer.offers || offer;
      // French locale uses offerLogoFr when present; otherwise the default offerLogo.
      var logo = (this.isFrench && o.offerLogoFr) ? o.offerLogoFr : o.offerLogo;
      if (!logo) return '';
      return (typeof logo === 'object') ? (logo.sourceUrl || '') : logo;
    },

    /**
     * Mirror of formatRateValue but for prefixed values like "$299". When the
     * user supplies HTML, we don't prepend "$" — they already wrote whatever
     * they wanted (e.g. "<strong>From $299</strong>").
     */
    formatAmountValue(val, prefix) {
      if (val === null || val === undefined || val === '') return '';
      val = String(val);
      if (/<[^>]+>/.test(val)) return val;
      return (prefix || '') + val;
    },

    // In French, use a comma as the decimal separator (8.29 → 8,29).
    frNum(val) {
      return this.isFrench ? String(val).replace(/\./g, ',') : String(val);
    },
    // OEM Offer Hero currency: "$8,999" in EN, "8 999 $" in FR (Canadian French puts the
    // dollar sign after and groups thousands with a space). Strips a leading $ the feed may already include.
    ohMoney(val) {
      if (val === null || val === undefined || val === '') return '';
      var s = String(val);
      if (/<[^>]+>/.test(s)) return s; // already markup/formatted
      s = s.replace(/^\s*\$\s*/, '');
      if (!this.isFrench) return '$' + s;
      return s.replace(/,(?=\d{3}(?!\d))/g, '\u00a0').replace(/\./g, ',') + '\u00a0$';
    },
    // Lease APR prefixed with "At" / "À" (e.g. "At 8.29%" / "À 8,29%"). When the lease
    // label already ENDS with that linking word ("Lease starting at" / "…à"), the prefix
    // is dropped so it isn't repeated — but a mid-sentence "at" (e.g. "Lease at payment
    // equivalent to") still gets the prefix.
    ohLeaseAprText(offer) {
      var apr = this.ohApr(offer, 'leaseApr');
      if (!apr) return '';
      var label = (this.offerVal(offer, 'leaseLabel', 'leaseLabelFr') || '').toString().replace(/<[^>]+>/g, '').trim().toLowerCase();
      if (/(^|\s)(at|à)$/.test(label)) return apr;
      return this.t('At', 'À') + ' ' + apr;
    },

    heroDesc(offer) {
      if (offer.is_static) return ''; // image-only — no overlay description
      if (offer.is_extra) return offer.description || '';
      if (this.isMinerva) {
        if (this.shouldHideOfferData(offer)) return '';
        var ov = this.slideOverride(offer);
        var ovDesc = this.isFrench ? (ov.description_fr || ov.description) : ov.description;
        if (ovDesc) return ovDesc;
        var o = offer.offers;
        var parts = [];

        // Finance info
        var finLabel = this.offerVal(offer, 'financeLabel', 'financeLabelFr');
        var finApr = this.offerVal(offer, 'financeApr');
        var finDisclaimer = this.t(o.financeDisclaimer, o.financeDisclaimerFr);
        if (finLabel || finApr) {
          var fin = (finLabel || (this.isFrench ? 'Financement' : 'Finance'));
          if (finApr) fin += ' ' + this.formatRateValue(finApr, '%');
          if (finDisclaimer) fin += ' ' + finDisclaimer;
          parts.push(fin);
        }

        // Lease info
        var leLabel = this.offerVal(offer, 'leaseLabel', 'leaseLabelFr');
        var leApr = this.offerVal(offer, 'leaseApr');
        var leDisclaimer = this.t(o.leaseDisclaimer, o.leaseDisclaimerFr);
        if (leLabel || leApr) {
          var le = (leLabel || (this.isFrench ? 'Location' : 'Lease'));
          if (leApr) le += ' ' + this.formatRateValue(leApr, '%');
          if (leDisclaimer) le += ' ' + leDisclaimer;
          parts.push(le);
        }

        // Payment info
        var payAmount = this.offerVal(offer, 'paymentAmount') || o.payment;
        if (payAmount) {
          var payLabel = this.offerVal(offer, 'paymentLabel', 'paymentLabelFr');
          var payDisclaimer = this.t(o.paymentDisclaimer, o.paymentDisclaimerFr);
          var pay = this.formatAmountValue(payAmount, '$');
          if (payLabel) pay += ' ' + payLabel;
          if (payDisclaimer) pay += ' ' + payDisclaimer;
          parts.push(pay);
        }

        // Down payment — falls back to Minerva values when no per-slide override is set.
        var dpAmount = this.offerVal(offer, 'downPayment') || o.downPayment;
        if (dpAmount) {
          var dpLabel = this.offerVal(offer, 'downPaymentLabel', 'downPaymentLabelFr')
                     || this.t(o.downPaymentLabel, o.downPaymentLabelFr);
          parts.push(this.ohMoney(dpAmount) + (dpLabel ? ' ' + dpLabel : ''));
        }

        // Normal offer lines
        var line1 = this.offerVal(offer, 'normalOfferLine1', 'normalOfferLine1Fr') || this.offerVal(offer, 'normal1', 'normal1Fr');
        var line2 = this.offerVal(offer, 'normalOfferLine2', 'normalOfferLine2Fr') || this.offerVal(offer, 'normal2', 'normal2Fr');
        if (line1) parts.push(line1);
        if (line2) parts.push(line2);

        return parts.join('<br>');
      }
      return offer.description || '';
    },

    // Custom description only — the override text (or an extra slide's own
    // description), WITHOUT the auto-built finance/lease/payment/down/rebate
    // lines. Those now render structured through the shared offer-block partial,
    // so the Hero Banner / Split / Media Columns / Carousel layouts use this to
    // avoid printing the same figures twice (badge + paragraph).
    slideCustomDesc(offer) {
      if (offer.is_static) return '';
      if (offer.is_extra) return offer.description || '';
      if (this.isMinerva) {
        if (this.shouldHideOfferData(offer)) return '';
        var ov = this.slideOverride(offer);
        return (this.isFrench ? (ov.description_fr || ov.description) : ov.description) || '';
      }
      return offer.description || '';
    },

    changeActiveOffer(index, type) {
      this.$set(this.activeOffer, index, type);
    },

    /**
     * Plain-text version of a possibly-HTML string — titles allow safe HTML
     * (spans/<br>), but alt attributes and other text-only sinks need it stripped.
     */
    plainText(html) {
      return String(html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    },

    /**
     * Content Position modifier for the overlay containers of the Carousel /
     * Hero Banner / Split / Media Columns layouts ('' = layout default). Only
     * Additional slides carry the setting; the OEM Offer Hero layout maps it
     * on its own __custom block instead. Also flags Additional slides with
     * `lbx-overlay--extra` so mobile can give their copy the same treatment
     * the Offer Hero layout uses (top-anchored, calmer type).
     */
    contentPosClass(offer) {
      if (!offer || !offer.is_extra) return '';
      var cls = 'lbx-overlay--extra';
      if (offer.content_position) cls += ' lbx-cpos--' + offer.content_position;
      return cls;
    },

    // Tap/click on the icon — toggles "sticky" mode. When sticky, the popup
    // stays open until the user taps outside or clicks the icon again. This is
    // the path mobile and keyboard users take (no hover signal there).
    toggleDisclaimer(index, e) {
      // A click is a deliberate action — kill any pending hover-close timer so
      // it can't fire a moment later and shut the popup the user just pinned.
      if (this._disclaimerCloseTimer) {
        clearTimeout(this._disclaimerCloseTimer);
        this._disclaimerCloseTimer = null;
      }
      if (this.activeDisclaimer === index && this.disclaimerSticky) {
        this.closeDisclaimer();
      } else {
        this.activeDisclaimer = index;
        this.disclaimerSticky = true;
        var wrap = e && e.currentTarget && e.currentTarget.closest
          ? e.currentTarget.closest('.lbx-slider__disclaimer')
          : null;
        if (wrap) this.measureDisclaimer(wrap);
      }
    },

    handleOutsideClick(e) {
      if (this.activeDisclaimer !== null && !e.target.closest('.lbx-slider__disclaimer, .lbx-slider__disclaimer-popup')) {
        this.closeDisclaimer();
      }
    },

    /**
     * Hide the SSR placeholder once Vue has taken over
     */
    hideSSR() {
      var instanceId = this.$el.getAttribute('data-instance');
      var ssrEl = document.getElementById(instanceId + '-ssr');
      if (ssrEl) ssrEl.style.display = 'none';
    },

    // ─── Countdown ─────────────────────────────────────────────
    startCountdown() {
      var self = this;
      var endStr = this.config.countdownEndDate;
      if (!endStr) {
        this.countdownExpired = true;
        return;
      }

      // Check start date visibility
      var startStr = this.config.countdownStartDate;
      if (startStr) {
        if (this.countdownMs('Start') > Date.now()) {
          this.countdownVisible = false;
          return;
        }
      }

      this.updateCountdown();
      this.countdownInterval = setInterval(function () {
        self.updateCountdown();
      }, 1000);
    },

    countdownMs(which) {
      var ts = this.config['countdown' + which + 'Ts'];
      if (ts) return ts;
      var str = this.config['countdown' + which + 'Date'];
      return str ? new Date(str.replace(' ', 'T')).getTime() : NaN;
    },

    updateCountdown() {
      var diff = this.countdownMs('End') - Date.now();

      if (diff <= 0) {
        this.countdownDays = '00';
        this.countdownHours = '00';
        this.countdownMinutes = '00';
        this.countdownSeconds = '00';
        this.countdownExpired = true;
        if (this.countdownInterval) {
          clearInterval(this.countdownInterval);
          this.countdownInterval = null;
        }
        return;
      }

      var d = Math.floor(diff / 86400000);
      var h = Math.floor((diff % 86400000) / 3600000);
      var m = Math.floor((diff % 3600000) / 60000);
      var s = Math.floor((diff % 60000) / 1000);

      this.countdownDays = d < 10 ? '0' + d : String(d);
      this.countdownHours = h < 10 ? '0' + h : String(h);
      this.countdownMinutes = m < 10 ? '0' + m : String(m);
      this.countdownSeconds = s < 10 ? '0' + s : String(s);
    },
  },
};
